import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
} from "recharts";
import { useWavesurfer } from "@wavesurfer/react";
//@ts-ignore
import Spectrogram from "wavesurfer.js/dist/plugins/spectrogram.esm.js";
import { ReactComponent as Play } from "../assets/icons/play.svg";
import { ReactComponent as Pause } from "../assets/icons/pause.svg";
import BackArrow from "../assets/images/back-arrow.svg";
import CardiovascularObservationResult from "./CardiovascularObservationResult";
import { Tab } from "@headlessui/react";
import { constants } from "../utils/ranges";
import { useAuth } from "../utils/auth";

import { ReactComponent as Download } from "../assets/icons/Download.svg";
import CarePlixLogo from "../assets/images/CarePlixLogo.png";
import CoverImage from "../assets/images/coverImage.png";
import BodyVitalReportImage from "../assets/images/BodyVitalReportImage.png";
import Heart from "../assets/images/heart.png";
import OxygenSaturation from "../assets/images/OxygenSaturation.png";
import HeartRate from "../assets/images/HeartRate.png";
import RespirationRate from "../assets/images/RespirationRate.png";
import HeartRateMax from "../assets/images/HeartRateMax.png";
import PNN50 from "../assets/images/PNN50.png";
import CardiacOutput from "../assets/images/CardiacOutput.png";
import MeanArterialPressure from "../assets/images/MeanArterialPressure.png";
import HeartUtilization from "../assets/images/HeartUtilization.png";
import HeartRateReserve from "../assets/images/HeartRateReserve.png";
import TargetHeartRateRange from "../assets/images/TargetHeartRateRange.png";
import VO2Max from "../assets/images/VO2Max.png";
import TotalBodyWater from "../assets/images/TotalBodyWater.png";
import TotalBodyFat from "../assets/images/TotalBodyFat.png";
import BloodVolume from "../assets/images/BloodVolume.png";
import BodyWater from "../assets/images/BodyWater.png";
import BloodPressure from "../assets/images/BloodPressure.png";
import Physiological from "../assets/images/Physiological.png";
import BodyWeight from "../assets/images/BodyWeight.png";
import stress from "../assets/images/stress.png";

import {
  Font,
  Document,
  Page,
  StyleSheet,
  View,
  Text,
  Image,
  PDFDownloadLink,
} from "@react-pdf/renderer";
import DermaResult from "./DermaResult";
import VisionResult from "./VisionResult";
import AssessmentResult from "./AssessmentResult";
import PIVCResult from "./PIVCResult";

Font.register({
  family: "Poppins",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLDz8V1tvFP-KUEg.ttf",
      fontWeight: "light",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiEyp8kv8JHgFVrFJDUc1NECPY.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLGT9V1tvFP-KUEg.ttf",
      fontWeight: "medium",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLEj6V1tvFP-KUEg.ttf",
      fontWeight: "semibold",
    },
    {
      src: "https://fonts.gstatic.com/s/poppins/v20/pxiByp8kv8JHgFVrLCz7V1tvFP-KUEg.ttf",
      fontWeight: "bold",
    },
  ],
});

const PDFReport = ({
  name = "",
  age = 0,
  gender = "",
  height = 0,
  weight = 0,
  result = {
    timestamp: new Date(),
    posture: "resting",
    bp_sys: 0,
    bp_dia: 0,
    resp_rate: 0,
    oxy_sat_prcnt: 0,
    heart_rate: 0,
    bmi: 0,
    sdnn: 0,
    rmssd: 0,
    pNN50_per: 0,
    cardiac_out: 0.0,
    map: 0,
    heart_utilized: 0,
    HRMax: 0,
    HRR: 0,
    THRR: 0,
    vo2max: 0.0,
    bloodvolume: 0.0,
    tbw: 0.0,
    tbwp: 0.0,
    bodyfat: 0.0,
    weight: 0.0,
    overallScore: 0,
    pulmoScore: "",
    tbf: 0.0,
    emm: 0.0,
    fitnessLevel: "",
    prq: 0.0,
    fev1fvcRatio: 0.0,
    isGood: "",
    hba1c: 0,
  },
}) => {
  const styles = StyleSheet.create({
    page: {
      backgroundColor: "#fff",
      flexDirection: "column",
      alignItems: "stretch",
      justifyContent: "space-between",
    },
    header: {
      backgroundColor: "#f7f7f7",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: 24,
      paddingVertical: 16,
      borderBottom: 1,
      borderBottomColor: "#D3D3D3",
    },
    contentContainer: { flex: 1, paddingVertical: 12, paddingHorizontal: 24 },
    rowContainer: { flexDirection: "row", justifyContent: "space-between" },
    largeText: { fontFamily: "Poppins", fontSize: 15, fontWeight: "bold" },
    normalText: { fontFamily: "Poppins", fontSize: 12, fontWeight: "semibold" },
    smallText: { fontFamily: "Poppins", fontSize: 10 },
    footer: {
      height: 30,
      backgroundColor: "#192852",
      width: "100%",
    },
    coverPage: {
      backgroundColor: "#000068",
      padding: 0,
      height: "100%",
    },
    // coverImageContainer: {
    //   width: "100%",
    //   height: "80%",
    //   overflow: "hidden",
    //   position: "relative",
    //   backgroundColor: "#000068",
    // },
    coverImageContainer: {
      width: "100%",
      height: "100%",
      backgroundColor: "#000068",
      alignItems: "center", // center horizontally
      justifyContent: "center", // center vertically
    },

    // coverImage: {
    //   width: "100%",
    //   height: "100%",
    //   objectFit: "cover",
    //   justifyContent: "center",
    // },
    coverImage: {
      width: "100%", // make smaller (set your own size)
      height: "80%",
      resizeMode: "contain", // prevent stretching
    },
    coverLogo: {
      position: "absolute",
      top: 20,
      left: 20,
      width: 220,
      height: 75,
      objectFit: "contain",
      backgroundColor: "#FFF",
      borderRadius: 15,
    },
    coverTitleArea: {
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: "#F4F7F8",
      paddingVertical: 10,
      paddingHorizontal: 64,
      borderTopLeftRadius: 64,
      borderTopRightRadius: 64,
    },
    reportTitle: {
      fontSize: 24,
      fontWeight: "normal",
      textAlign: "center",
      marginBottom: 4,
      fontFamily: "Poppins",
    },
    reportSubtitle: {
      fontSize: 36,
      fontWeight: "bold",
      textAlign: "center",
      color: "#00ABFF",
      fontFamily: "Poppins",
    },
    headerText: {
      fontFamily: "Poppins",
      fontSize: 10,
      color: "#666",
    },
    circleContainer: {
      width: 130,
      height: 130,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#E9F3FF",
      borderRadius: 25,
      marginRight: 15,
      paddingVertical: 5,
      paddingHorizontal: 5,
    },

    titleText: {
      fontFamily: "Poppins",
      fontSize: 13,
      fontWeight: "600",
      color: "#1A1A1A",
      textAlign: "center",
      marginBottom: 8,
    },

    statusBox: {
      width: 80,
      height: 50,
      backgroundColor: "#FFFFFF",
      borderRadius: 30,
      justifyContent: "center",
      alignItems: "center",
      shadowColor: "#000",
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },

    scoreText: {
      fontFamily: "Poppins",
      fontSize: 10,
      fontWeight: "400",
      color: "#5A5A5A",
      textAlign: "center",
    },
    tableContainer: {
      marginTop: 10,
      borderRadius: 10,
      overflow: "hidden",
    },
    tableHeaderRow: {
      flexDirection: "row",
      backgroundColor: "#2E5894",
      color: "#fff",
      paddingVertical: 5,
      paddingHorizontal: 12,
    },
    tableHeaderCell: {
      fontFamily: "Poppins",
      fontSize: 12,
      fontWeight: "bold",
      color: "#fff",
      flex: 1,
    },
    tableRow: {
      flexDirection: "row",
      backgroundColor: "#fff",
      borderBottom: "1px solid #eee",
      paddingVertical: 8,
      paddingHorizontal: 12,
      alignItems: "center",
    },
    tableCell: {
      fontFamily: "Poppins",
      fontSize: 12,
      color: "#333",
      flex: 1,
    },
    adjustedMuscleMassRow: {
      flexDirection: "row",
      backgroundColor: "#2E5894",
      color: "#fff",
      paddingVertical: 4,
      paddingHorizontal: 12,
      borderRadius: 10,
    },
    adjustedCell: {
      fontFamily: "Poppins",
      fontSize: 12,
      fontWeight: "bold",
      color: "#fff",
    },
    disclaimerContainer: {
      marginTop: 30,
      padding: 15,
      borderRadius: 10,
      backgroundColor: "#F4F7F8",
      border: "1px solid #eee",
    },
    disclaimerText: {
      fontFamily: "Poppins",
      fontSize: 9,
      fontWeight: "normal",
      color: "#666",
      textAlign: "justify",
    },
    bodyVitalsContainer: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: "#2E5894",
      borderRadius: 12,
      paddingHorizontal: 10,
      paddingVertical: 8,
      width: "100%",
    },
    textContainer: {
      flex: 1,
    },
    bodyVitalstitle: {
      fontSize: 16,
      fontWeight: 700,
      color: "#FFFFFF",
      marginBottom: 4,
    },
    highlight: {
      color: "#4BA3F2",
    },
    bodyVitalSubtitle: {
      fontSize: 10,
      color: "#DADADA",
    },
    bodyVitalsImage: {
      width: 70,
      height: 40,
      objectFit: "contain",
    },
    stressBox: {
      marginTop: 15,
      padding: 15,
      borderRadius: 10,
      backgroundColor: "#f7f7f7",
      border: "1px solid #eee",
    },
  });

  return (
    <Document
      title="CarePlix Report"
      subject="Report generated from CarePlix Vitals Scan"
      author="CarePlix"
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.coverPage}>
          <View style={styles.coverImageContainer}>
            <Image style={styles.coverImage} src={CoverImage} />
            <Image style={styles.coverLogo} src={CarePlixLogo} />
          </View>
          <View style={styles.coverTitleArea}>
            <Text style={styles.reportSubtitle}>Digital Health</Text>
            <Text style={styles.reportTitle}>
              Pulmonary Health & Vitals Report
            </Text>
          </View>
        </View>
      </Page>
      <Page style={styles.page}>
        <View fixed style={styles.header}>
          <View>
            <Text
              style={{ color: "#00ABFF", fontWeight: "bold", fontSize: 18 }}
            >
              Digital Health
            </Text>
            <Text style={{ fontWeight: "normal", fontSize: 20 }}>
              Pulmonary Health & Vitals Report
            </Text>
          </View>
          <Text style={styles.headerText}>
            Date & Time:{" "}
            {new Date(result.timestamp).toLocaleDateString("en-GB")}
            {", "}
            {new Date(result.timestamp).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })}
          </Text>
        </View>
        <View style={styles.contentContainer}>
          <View style={styles.rowContainer}>
            <View style={{ flex: 1.5, paddingRight: 40 }}>
              {[
                { label: "Name", value: name },
                { label: "Age", value: `${age} years` },
                { label: "Gender", value: gender },
                { label: "Height", value: `${height} cms` },
                { label: "Weight", value: `${weight} kgs` },
              ].map(({ label, value }) => (
                <Text
                  key={label.toLowerCase().replaceAll(" ", "-")}
                  style={[
                    styles.normalText,
                    {
                      fontWeight: "normal",
                      marginTop: label === "Name" ? 0 : 8,
                      borderBottom: 1,
                      borderBottomColor: "#E7E7E7",
                    },
                  ]}
                >
                  <Text style={{ fontWeight: "semibold", minWidth: 60 }}>
                    {label} :
                  </Text>
                  <Text
                    style={{
                      textTransform: label === "Name" ? "capitalize" : "none",
                    }}
                  >
                    {` ${value}`}
                  </Text>
                </Text>
              ))}
            </View>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "center",
                marginTop: 10,
              }}
            >
              {[
                {
                  title: "Overall Health Score",
                  score: `${result.overallScore}%`,
                },
                {
                  title: "Pulmonary Health Score",
                  score: `${Math.round(result.pulmoScore)}%`,
                },
              ].map((item) => (
                <View style={styles.circleContainer} key={item.title}>
                  <Text style={styles.titleText}>{item.title}</Text>
                  <View style={styles.statusBox}>
                    <Text style={styles.scoreText}>{item.score}</Text>
                    {/* <Text style={styles.subtext}>{item.status}</Text> */}
                  </View>
                </View>
              ))}
            </View>
          </View>

          <Text
            style={{
              fontWeight: "semibold",
              fontSize: 12,
              marginTop: 20,
              marginBottom: 10,
            }}
          >
            Your Pulmomary Observation
          </Text>

          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2.5 }]}>
                Parameter
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1 }]}>Status</Text>
            </View>

            {[
              {
                label: "Predicted FEV1/FVC",
                value: result?.fev1fvcRatio,
                status: {
                  label:
                    result?.fev1fvcRatio >= 0.7
                      ? "Normal"
                      : "Low – Possible Obstruction",
                  color: result?.fev1fvcRatio >= 0.7 ? "#59D341" : "#FF2B2B",
                },
              },
              {
                label: "Obstructive Airway Disease (OAD)",
                value: "",
                status: {
                  label: result.isGood ? "Not Present" : "Present",
                  color: result.isGood ? "#59D341" : "#FF2B2B",
                },
              },
              (() => {
                const prq = parseFloat(result.prq);
                let status = {};

                if (prq >= 1.6 && prq <= 2.2) {
                  status = { label: "Normal", color: "#59D341" };
                } else if (prq > 2.2) {
                  status = {
                    label: "High",
                    color: "#FF8C00",
                  };
                } else if (prq < 1.4) {
                  status = {
                    label: "Low",
                    color: "#FF2B2B",
                  };
                } else {
                  status = { label: "Normal", color: "#FF8C00" };
                }

                return {
                  label: "Pulse-Respiration Quotient (PRQ)",
                  value: result.prq,
                  status,
                };
              })(),
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text style={[styles.tableCell, { flex: 2.5 }]}>
                  {item.label}
                </Text>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>
                  {item.value}
                </Text>
                <View
                  style={[
                    styles.smallText,

                    styles.tableCell,
                    {
                      alignItems: "center",
                      fontFamily: "Poppins",
                      fontSize: 10,
                      flex: 1,
                    },
                  ]}
                >
                  <Text style={{ color: item.status?.color || "#000" }}>
                    {item.status.label}
                  </Text>
                </View>
              </View>
            ))}
          </View>

          <View style={styles.disclaimerContainer}>
            <Text
              style={[
                styles.largeText,
                { marginTop: 2, marginBottom: 8, fontSize: 10 },
              ]}
            >
              Disclaimer
            </Text>
            <Text style={styles.disclaimerText}>
              For <Text style={styles.boldText}>Investigational Use Only</Text>.
              This scan is not a substitute for the clinical judgement of a
              health care professional. This scan is intended to improve you
              awareness of general wellness. This scan does not diagnose, treat,
              mitigate or prevent any disease, symptom, disorder or abnormal
              physical state. Consult with a healthcare professional or
              emergency services if you believe you may have a medical issue.
            </Text>
          </View>
        </View>
        <View style={styles.contentContainer} break>
          <View style={styles.bodyVitalsContainer}>
            <View style={styles.textContainer}>
              <Text style={styles.bodyVitalstitle}>
                Key Body <Text style={styles.highlight}>Vitals</Text>
              </Text>
              <Text style={styles.bodyVitalSubtitle}>
                Monitoring essential health metrics for optimal wellness
              </Text>
            </View>

            <Image src={BodyVitalReportImage} style={styles.bodyVitalsImage} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                Key Body Vitals
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                Normal Range
              </Text>
            </View>
            {[
              {
                label: "Blood Pressure",
                value: `${result.bp_sys}/${result.bp_dia} mmHg`,
                status: (() => {
                  const sysStatus = constants[
                    "Blood Pressure"
                  ].ranges[0].reduce((p, c) =>
                    result.bp_sys >= c.point ? c : p
                  ) || { label: "Unknown", color: "#000" };

                  const diaStatus = constants[
                    "Blood Pressure"
                  ].ranges[1].reduce((p, c) =>
                    result.bp_dia >= c.point ? c : p
                  ) || { label: "Unknown", color: "#000" };

                  const sysIndex = constants[
                    "Blood Pressure"
                  ].ranges[0].findIndex((r) => r.label === sysStatus.label);
                  const diaIndex = constants[
                    "Blood Pressure"
                  ].ranges[1].findIndex((r) => r.label === diaStatus.label);

                  return sysIndex > diaIndex ? sysStatus : diaStatus;
                })(),
                range: "90–120/60–80 mmHg",
              },

              {
                label: "Heart Rate",
                value: `${result.heart_rate} BPM`,
                status: constants["Heart Rate"].ranges["resting"]?.reduce?.(
                  (p, c) => (result.heart_rate >= c.point ? c : p)
                ) || { label: "Unknown", color: "#000" },
                range: "60 - 100 BPM",
              },
              {
                label: "Respiration Rate",
                value: `${result.resp_rate} breaths/min`,
                status: constants["Respiration Rate"].ranges.reduce((p, c) =>
                  result.resp_rate >= c.point ? c : p
                ) || { label: "Unknown", color: "#000" },
                range: "12 - 20/min",
              },
              {
                label: "Oxygen Saturation",
                value: `${result.oxy_sat_prcnt} %`,
                status: constants["Oxygen Saturation"].ranges.reduce((p, c) =>
                  result.oxy_sat_prcnt >= c.point ? c : p
                ) || { label: "Unknown", color: "#000" },
                range: "95 - 100 %",
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 2, fontWeight: "semibold" },
                  ]}
                >
                  {item.label}
                </Text>
                <View style={{ flex: 1.5, flexDirection: "column" }}>
                  <Text
                    style={[styles.tableCell, { padding: 0, borderBottom: 0 }]}
                  >
                    {item.value}
                  </Text>
                  <Text
                    style={[
                      styles.smallText,
                      {
                        fontFamily: "Poppins",
                        fontSize: 8,
                        color: item.status?.color || "#000",
                        fontWeight: "bold",
                        marginTop: 20,
                      },
                    ]}
                  >
                    {item.status?.label || ""}
                  </Text>
                </View>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>
                  {item.range}
                </Text>
              </View>
            ))}
          </View>

          <Text style={[styles.largeText, { marginTop: 15, marginBottom: 10 }]}>
            Health Parameters Description
          </Text>
          {[
            {
              icon: BloodPressure,
              title: "Blood Pressure",
              desc: "Blood pressure is a measure of the force that the heart uses to pump blood around the body. Blood pressure is measured in millimeters of mercury (mmHg) and is given as 2 figures:\n\u2022 Systolic pressure - the pressure when your heart pushes blood out\n\u2022 Diastolic pressure - the pressure when your heart rests between beats",
            },
            {
              icon: HeartRate,
              title: "Heart Rate",
              desc: "The heart rate is the number of times heart beats in a minute (bp 4). A normal resting heart rate for adults ranges from 60 to 100 beats per minute. A person's resting heart rate is the heart rate during physical activity and exercise. A normal resting heart rate for adults ranges from 60 to 100 bpm.",
            },
            {
              icon: RespirationRate,
              title: "Respiration Rate",
              desc: "The respiration rate is the number of breaths taken in a minute. The rate is usually measured when a person is at rest and simply involves counting the number of breaths for one minute by counting how many times the chest rises.",
            },
            {
              icon: OxygenSaturation,
              title: "Oxygen Saturation",
              desc: "Oxygen saturation (also known as SpO2) is the measurement used to assess the amount of oxygen being carried by the blood and delivered to body's tissues.",
            },
          ].map(({ title, desc, icon }) => (
            <View
              key={`info-${title.toLowerCase().replaceAll(" ", "-")}`}
              style={{ marginBottom: 10, flexDirection: "row" }}
              wrap={false}
            >
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 6,
                  backgroundColor: "#EAF5FF",
                  border: "1px solid #A7CDF3",
                  justifyContent: "center",
                  alignItems: "center",
                  marginRight: 8,
                }}
              >
                <Image
                  src={icon}
                  style={{
                    width: 20,
                    height: 20,
                  }}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text
                  style={[
                    styles.normalText,
                    { fontWeight: "bold", fontSize: 12, color: "#333" },
                  ]}
                >
                  {title}
                </Text>
                <Text
                  style={[
                    styles.smallText,
                    {
                      fontWeight: "light",
                      textAlign: "justify",
                      marginTop: 2,
                      color: "#666",
                    },
                  ]}
                >
                  {desc}
                </Text>
              </View>
            </View>
          ))}
        </View>

        <View style={styles.contentContainer} break>
          <View style={styles.bodyVitalsContainer}>
            <View style={styles.textContainer}>
              <Text style={styles.bodyVitalstitle}>
                Heart <Text style={styles.highlight}>Health</Text>
              </Text>
              <Text style={styles.bodyVitalSubtitle}>
                Understanding key heart health metrics.
              </Text>
            </View>

            <Image src={Heart} style={styles.bodyVitalsImage} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                Heart Health
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                Normal Range
              </Text>
            </View>
            {[
              {
                label: "SDNN",
                value: `${parseFloat(result.sdnn).toFixed(2)} ms`,
                range: "30 - 95 ms",
                status:
                  result.sdnn !== "--"
                    ? constants["SDNN"].ranges.reduce((p, c) =>
                        result.sdnn >= c.point ? c : p
                      )
                    : undefined,
              },
              {
                label: "RMSSD",
                value: `${parseFloat(result.rmssd).toFixed(2)} ms`,
                range: "20 - 89 ms",
                status:
                  result.rmssd !== "--"
                    ? constants["RMSSD"].ranges.reduce((p, c) =>
                        result.rmssd >= c.point ? c : p
                      )
                    : undefined,
              },
              {
                label: "PNN50",
                value: `${parseFloat(result.pNN50_per).toFixed(2)} %`,
                range: "5 - 40 %",
                status:
                  result.pNN50_per !== "--"
                    ? constants["PNN50"].ranges.reduce((p, c) =>
                        result.pNN50_per >= c.point ? c : p
                      )
                    : undefined,
              },
              {
                label: "Cardiac Output",
                value: `${parseFloat(result.cardiac_out).toFixed(2)} L/min`,
                range: "4 - 7 L/min",
                status:
                  result.cardiac_out !== "--"
                    ? constants["Cardiac Output"].ranges.reduce((p, c) =>
                        result.cardiac_out >= c.point ? c : p
                      )
                    : undefined,
              },
              {
                label: "Mean Arterial Pressure",
                value: `${result.map} mmHg`,
                range: "70 - 100 mmHg",
                status:
                  result.map !== "--"
                    ? constants["Mean Arterial Pressure"].ranges.reduce(
                        (p, c) => (result.map >= c.point ? c : p)
                      )
                    : undefined,
              },
              {
                label: "Heart Utilized",
                value: `${result.heart_utilized} %`,
                range: "41 - 70 %",
                status:
                  result.heart_utilized !== "--"
                    ? constants["Heart Utilized"].ranges.reduce((p, c) =>
                        result.heart_utilized >= c.point ? c : p
                      )
                    : undefined,
              },
              {
                label: "Heart Rate Max",
                value: `${result.HRMax} BPM`,
                range: "NA",
                status: { label: "", color: "" },
              },
              {
                label: "Heart Rate Reserve",
                value: `${result.HRR} BPM`,
                range: "NA",
                status: { label: "", color: "" },
              },
              {
                label: "Target Heart Rate Range",
                value: `${result.THRR} BPM`,
                range: "NA",
                status: { label: "", color: "" },
              },
              {
                label: "VO2 Max",
                value: `${parseFloat(result.vo2max).toFixed(2)} mL/kg/min`,
                range: `42.5 mL/kg/min (Male)\n33 mL/kg/min (Female)`,
                status: { label: "", color: "" },
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 2.5, fontWeight: "semibold" },
                  ]}
                >
                  {item.label}
                </Text>
                <View style={[styles.tableCell, { flex: 1.5 }]}>
                  <Text>{item.value}</Text>
                  {item.status.label && (
                    <Text
                      style={{
                        fontFamily: "Poppins",
                        fontSize: 9,
                        fontWeight: "bold",
                        color: item.status.color,
                        marginTop: 2,
                      }}
                    >
                      {item.status.label}
                    </Text>
                  )}
                </View>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 1.5, fontSize: item.label === "VO2 Max" ? 9 : 11 },
                  ]}
                >
                  {item.range}
                </Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.contentContainer} break>
          {[
            {
              icon: HeartRate,
              title: "SDNN",
              desc: "The standard deviation of all of the NN intervals  (the time intervals between each heartbeat). Higher  numbers usually indicate that your body is coping  better with stress.",
            },
            {
              icon: HeartRate,
              title: "RMSSD",
              desc: "The root mean square of successive intervals be tween normal heartbeats (RMSSD) and is another  measure of heart rate variability ( HRV). RMSSD is  obtained by first calculating each successive time  intervals between heartbeats in msec.",
            },
            {
              icon: PNN50,
              title: "PNN50",
              desc: "PNN50 is a measure of the number of short term fluc tuations in the heart rate and is considered to reflect  changes in (HRV)  driven by the sympathetic branch  of the autonomic nervous system, that prepares the  body for stress and physical activity. PNN50 lower  than 3% is indicative of high risk.",
            },
            {
              icon: CardiacOutput,

              title: "Cardiac Output",
              desc: "The amount of blood  the heart pumps through the  circulatory system in a minute. Cardiac output is  generally 4-8 L/min in an rest.",
            },
            {
              icon: MeanArterialPressure,
              title: "Mean Arterial Pressure",
              desc: "MAP is the average arterial pressure throughout one  heart cycle. MAP is an important measurement that  accounts for flow, resistance, and pressure with in your arteries. It allows doctors to evaluate how  well blood flows through your body and whether it's  reaching all your major organs.",
            },
            {
              icon: HeartUtilization,
              title: "Heart Utilization",
              desc: "Heart utilization is the percentage of overall heart us age to its maximum peak heart rate. Generally < 50%  utilization at rest and 51-85% utilization at moderate  intensity is considered as normal.",
            },
            {
              icon: HeartRateMax,
              title: "Heart Rate Max",
              desc: "Maximum heart rate is the highest number of beats your heat can pump per  minute when it's under high stress (physical or otherwise). Knowing your max  HR can help you track your fitness progress and determine your target heart  rate for exercise intensity.",
            },
            {
              icon: HeartRateReserve,
              title: "Heart Rate Reserve",
              desc: "Heart rate reserve is the difference between your maximum (peak) heart rate  and your resting heart rate. The more fit you are, the lower your resting heart  rate will be. When you have a low resting heart rate, your heart rate reserve is  high.",
            },
            {
              icon: TargetHeartRateRange,
              title: "Target Heart Rate Range",
              desc: " To achieve a certain level of exertion, there is a minimum number of heartbeats  required within a specific timeframe.",
            },
            {
              icon: VO2Max,
              title: "VO2Max",
              desc: "VO2 Max is the maximum rate of oxygen consumption measured during incre mental exercise. Maximal oxygen consumption reflects your overall cardiores piratory fitness, and is an excellent way to assess your fitness level.\n A good V02 max for a man between ages 30 and 39 is 41 to 44.9 For women of the same age, a good V02 max is between 31.5 to 35.6",
            },
          ].map(({ title, desc, icon }) => (
            <View
              key={`info-${title.toLowerCase().replaceAll(" ", "-")}`}
              style={{ marginBottom: 10, flexDirection: "row" }}
              wrap={false}
            >
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 6,
                  backgroundColor: "#EAF5FF",
                  border: "1px solid #A7CDF3",
                  justifyContent: "center",
                  alignItems: "center",
                  marginRight: 8,
                }}
              >
                <Image
                  src={icon}
                  style={{
                    width: 20,
                    height: 20,
                  }}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Text
                  style={[
                    styles.normalText,
                    { fontWeight: "bold", fontSize: 12, color: "#333" },
                  ]}
                >
                  {title}
                </Text>
                <Text
                  style={[
                    styles.smallText,
                    {
                      fontWeight: "light",
                      textAlign: "justify",
                      marginTop: 2,
                      color: "#666",
                    },
                  ]}
                >
                  {desc}
                </Text>
              </View>
            </View>
          ))}
        </View>

        {/* physiological */}

        <View style={styles.contentContainer} break>
          <View style={styles.bodyVitalsContainer}>
            <View style={styles.textContainer}>
              <Text style={styles.bodyVitalstitle}>
                <Text style={styles.highlight}>Physiological</Text>
              </Text>
              <Text style={styles.bodyVitalSubtitle}>
                *Calculated based on height and weight inputted by you.
              </Text>
            </View>

            <Image src={Physiological} style={styles.bodyVitalsImage} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                Physiological
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                Normal Range
              </Text>
            </View>
            {[
              {
                label: "BMI",
                value: `${result.bmi} kg/m²`,
                status: constants["BMI"].ranges.reduce((p, c) =>
                  result.bmi > c.point ? c : p
                ),
                range: "18.5-24.9 kg/m²",
              },
              {
                label: "Blood Volume",
                value: `${result?.bloodvolume}/ml`,
                range: "NA",
              },
              {
                label: "Total Body Water",
                value: `${result?.tbw}/Liters`,
                range: "NA",
              },
              {
                label: "Body Water %",
                value: `${result?.tbwp}/%`,
                range: "NA",
              },
              {
                label: "Total Body Fat",
                value: `${result?.bodyfat}%`,
                range: "NA",
              },
              {
                label: "Body Weight",
                value: `${result?.weight}/kg`,
                range: "NA",
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 2, fontWeight: "semibold" },
                  ]}
                >
                  {item.label}
                </Text>
                <View style={{ flex: 1.5, flexDirection: "column" }}>
                  <Text
                    style={[styles.tableCell, { padding: 0, borderBottom: 0 }]}
                  >
                    {item.value}
                  </Text>
                  {item.status && (
                    <Text
                      style={[
                        styles.smallText,
                        {
                          fontFamily: "Poppins",
                          fontSize: 9,
                          color: item.status.color || "#000",
                          fontWeight: "bold",
                          marginTop: 20,
                        },
                      ]}
                    >
                      {item.status.label || ""}
                    </Text>
                  )}
                </View>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>
                  {item.range}
                </Text>
              </View>
            ))}
          </View>

          <Text style={[styles.largeText, { marginTop: 15, marginBottom: 10 }]}>
            Health Parameters Description
          </Text>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "space-between",
              marginTop: 10,
            }}
          >
            {[
              {
                icon: HeartRate,
                title: "BMI",
                desc: "Body Mass Index (BMI) is a measurement based on your weight and height. It is more of an indicator than a direct measurement of your total body fat.",
              },
              {
                icon: TotalBodyWater,
                title: "Total Body Water",
                desc: "Total Body Water is the total amount of water in the human body. This includes fluids both inside and outside of cells.",
              },
              {
                icon: TotalBodyFat,
                title: "Total Body Fat",
                desc: "Body fat percentage is an indicator of your body composition and indicates the amount of fat you have in your body.",
              },
              {
                icon: BloodVolume,
                title: "Blood Volume",
                desc: "Blood volume refers to the total amount of fluid circulating within the arteries, capillaries, veins, venules, and chambers of the heart at any time.",
              },
              {
                icon: BodyWater,
                title: "Body Water %",
                desc: "Most of the human body is water, with an average of roughly 60%. The amount of water in the body changes slightly with age, sex, and hydration levels.",
              },
              {
                icon: BodyWeight,
                title: "Body Weight",
                desc: "Body weight refers to the total mass of a person's body, which is composed of various tissues, such as bones, muscles, organs, and body fat.",
              },
            ].map(({ title, desc, icon }, index) => (
              <View
                key={index}
                style={{
                  width: "47%",
                  borderRadius: 8,
                  padding: 8,
                  marginBottom: 12,
                  flexDirection: "row",
                  alignItems: "flex-start",
                }}
                wrap={false}
              >
                <View
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 6,
                    backgroundColor: "#EAF5FF",
                    border: "1px solid #A7CDF3",
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: 8,
                  }}
                >
                  <Image src={icon} style={{ width: 26, height: 26 }} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#1e293b",
                      marginBottom: 3,
                    }}
                  >
                    {title}
                  </Text>
                  <Text
                    style={{
                      fontSize: 9,
                      lineHeight: 1.3,
                      color: "#475569",
                      textAlign: "justify",
                    }}
                  >
                    {desc}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* Stress */}

        <View style={styles.contentContainer} break>
          {/* Blood Biomakers */}

          <View style={[styles.bodyVitalsContainer]}>
            <View style={styles.textContainer}>
              <Text style={styles.bodyVitalstitle}>
                Blood <Text style={styles.highlight}>Biomarkers</Text>
              </Text>
              <Text style={styles.bodyVitalSubtitle}>
                Understand Blood Biomarkers(HbA1c Level).
              </Text>
            </View>

            <Image src={stress} style={styles.bodyVitalsImage} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                HbA1c Level
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                Normal Range
              </Text>
            </View>
            {[
              {
                label: "HbA1c",
                value: result.hba1c,
                status:
                  constants["HbA1c"].ranges
                    .slice()
                    .reverse()
                    .find((r) => result.hba1c >= r.point) || {},
                range: "< 5.7%",
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 2, fontWeight: "semibold" },
                  ]}
                >
                  {item.label}
                </Text>
                <View style={{ flex: 1.5, flexDirection: "column" }}>
                  <Text
                    style={[styles.tableCell, { padding: 0, borderBottom: 0 }]}
                  >
                    {item.value}
                  </Text>
                  <Text
                    style={[
                      styles.smallText,
                      {
                        fontFamily: "Poppins",
                        fontSize: 9,
                        color: item.status.color || "#000",
                        fontWeight: "bold",
                        marginTop: 20,
                      },
                    ]}
                  >
                    {item.status.label || ""}
                  </Text>
                </View>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>
                  {item.range}
                </Text>
              </View>
            ))}
          </View>
          <View style={styles.stressBox}>
            <Text style={[styles.largeText, { fontSize: 10 }]}>
              Your HbA1c Level is{" "}
              {constants["HbA1c"].ranges
                .slice()
                .reverse()
                .find((r) => result.hba1c >= r.point)?.label || "Low"}
            </Text>
            <Text style={styles.smallText}>
              When sugar enters the bloodstream, it binds to hemoglobin, this
              process is called glycation. The HBA1C measures the percentage of
              glycated hemoglobin levels in the blood for the last two to three
              months.
            </Text>
          </View>
          {/* stress */}
          <View style={[styles.bodyVitalsContainer, { marginTop: 60 }]}>
            <View style={styles.textContainer}>
              <Text style={styles.bodyVitalstitle}>
                Stress <Text style={styles.highlight}>Level</Text>
              </Text>
              <Text style={styles.bodyVitalSubtitle}>
                *Calculated based on height and weight inputted by you.
              </Text>
            </View>

            <Image src={stress} style={styles.bodyVitalsImage} />
          </View>
          <View style={styles.tableContainer}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>
                Stress Level
              </Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>Value</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>
                Normal Range
              </Text>
            </View>
            {[
              {
                label: "Stress Index",
                value: result.stress,
                status:
                  constants["Stress Level"].ranges
                    .slice()
                    .reverse()
                    .find((r) => result.stress >= r.point) || {},
                range: "NA",
              },
            ].map((item, index) => (
              <View style={styles.tableRow} key={index}>
                <Text
                  style={[
                    styles.tableCell,
                    { flex: 2, fontWeight: "semibold" },
                  ]}
                >
                  {item.label}
                </Text>
                <View style={{ flex: 1.5, flexDirection: "column" }}>
                  <Text
                    style={[styles.tableCell, { padding: 0, borderBottom: 0 }]}
                  >
                    {item.value}
                  </Text>
                  <Text
                    style={[
                      styles.smallText,
                      {
                        fontFamily: "Poppins",
                        fontSize: 9,
                        color: item.status.color || "#000",
                        fontWeight: "bold",
                        marginTop: 20,
                      },
                    ]}
                  >
                    {item.status.label || ""}
                  </Text>
                </View>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>
                  {item.range}
                </Text>
              </View>
            ))}
          </View>
          <View style={styles.stressBox}>
            <Text style={[styles.largeText, { fontSize: 10 }]}>
              Your Stress Level is{" "}
              {constants["Stress Level"].ranges
                .slice()
                .reverse()
                .find((r) => result.stress >= r.point)?.label || "Low"}
            </Text>
            <Text style={styles.smallText}>
              The body's reaction to a challenge or demand. There are 5 levels
              of stress.
            </Text>
          </View>

          <View style={styles.disclaimerContainer}>
            <Text
              style={[
                styles.largeText,
                { marginTop: 10, marginBottom: 5, fontSize: 10 },
              ]}
            >
              Disclaimer
            </Text>
            <Text style={styles.disclaimerText}>
              For <Text style={styles.boldText}>Investigational Use Only</Text>.
              This scan is not a substitute for the clinical judgment of a
              health care professional. This scan is intended to improve your
              awareness of general wellness. This scan does not diagnose, treat,
              mitigate or prevent any disease, symptom, disorder or abnormal
              physical state. Consult with a healthcare professional or
              emergency services if you believe you may have a medical issue
            </Text>
          </View>
        </View>
        <View fixed style={styles.footer} />
      </Page>
    </Document>
  );
};

const ScoreData = ({ isGood, fev1fvcRatio, pulmoScore }) => {
  return (
    <>
      <div className="score-card px-5 py-5 w-full bg-[#1D1D1D] rounded-2xl relative z-50 mb-6">
        <div className="flex flex-row justify-between items-center mb-3">
          <h4 className="text-xl font-semibold leading-5 from-[#455C8E] via-[#455C8E] to-[#A7D4FF] bg-gradient-to-r bg-clip-text text-transparent">
            Pulmonary Health Score
          </h4>
        </div>

        <p className="text-xxs text-white/70 text-justify mt-1 mb-4">
          {" "}
          Indicates your overall lung function health based on cough analysis,
          breathing pattern, and voice sample combined with your FEV1/FVC ratio.
        </p>

        <div className="progress-col relative mt-14 mb-1">
          <div className="progress bg-gradient-to-l from-[#64BD40] via-[#FFC444] to-[#C81616]">
            <div
              className="progress-bar"
              style={{
                width: `${Math.floor(pulmoScore)}%`,
              }}
            >
              <div className="progress-value">{Math.floor(pulmoScore)}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="circle-blur lg:w-96 lg:h-96 md:w-96 md:h-96 w-80 h-80 mx-auto absolute left-0 right-0 top-20 blur-[90px]" />
      <h1 className="mt-0 text-2xl relative z-50 text-center mb-5">
        {"Assessment indicates "}
        <span
          className="font-bold"
          style={{ color: isGood ? "#59D341" : "#FF2B2B" }}
        >
          {isGood && "no "}presence
        </span>
        {" of obstructive airway disease (OAD)"}
      </h1>
      <div
        className={`mt-4 p-4 relative z-50 rounded-2xl ${
          isGood ? "bg-success/35" : "bg-error/25"
        } text-sm`}
      >
        <h2 className="font-semibold">
          {isGood && "No "}Obstructive Airway Disease (OAD) Risk Found:
        </h2>
        <p className="mt-1 font-light">
          {"Your assessment shows "}
          <span className="font-semibold">{isGood && "no "}risk</span>
          {/* {` of having ${
            fev1fvcRatio < 0.65 ? "COPD" : "Asthma/COPD"
          } at this time.`} */}
          {` of having Asthma/COPD at this time.`}
        </p>
      </div>
      <div className="mt-5 p-6 rounded-[20px] bg-[#1D1D1D]/70 relative z-50">
        <h2 className="font-light text-secondary">
          Predicted FEV1/FVC:{" "}
          <span className="font-semibold text-white">{fev1fvcRatio}</span>
        </h2>
        <h3 className="mt-3 text-xs font-semibold">What is FEV1/FVC Value?</h3>
        <p className="mt-2 text-xxs relative z-50">
          The FEV1/FVC ratio is a key lung function indicator that compares the
          amount of air you can forcefully exhale in one second (FEV1) to the
          total amount of air you can exhale after taking a deep breath (FVC).
          <br />
          It helps assess how well your lungs are working and is commonly used
          to detect airflow obstruction in conditions like Asthma or COPD.
          <br />
          <br />✅ Normal Range: Typically above 70-80% (varies by age)
          <br />
          <br />✅ Lower Values: May indicate obstructive lung disease
        </p>
        <div className="circle-blur w-32 h-32 mx-auto absolute right-0 bottom-0 blur-[50px]"></div>
      </div>
      <div className="mt-6 p-6 rounded-[20px] bg-[#364560] text-wgite relative z-50">
        <h3 className="text-sm font-semibold text-secondary">
          For Investigational Use Only :
        </h3>
        <p className="mt-2 text-xxs">
          Careplix Pulmo is not a medical device and should not be used as a
          substitute for clinical judgment by a health care professional.
          CarePlix Pulmo is intended to Improve your awareness of your lung
          obstruction health and not intended to diagnose, treat, mitigate or
          prevent any disease, symptom, disorder or abnormal physical condition.
          Contact your health-care provider Immediately if you suspect that you
          have a medical problem. Statements regarding clinical data have not
          been evaluated by the US FDA and are only intended for self-awareness
          and general wellness purposes only.
        </p>
      </div>
    </>
  );
};

const AudioSample = ({ title, audioURL, onPoorQuality }) => {
  const waveformContainerRef = useRef(null);
  const { isReady, isPlaying, wavesurfer } = useWavesurfer({
    container: waveformContainerRef,
    url: audioURL,
    height: 40,
    interact: false,
    waveColor: "#fff",
    progressColor: "#808080",
    cursorColor: "#fff",
    cursorWidth: 2,
    barGap: 1,
    barWidth: 2,
    barRadius: 999,
  });
  const [quality, setQuality] = useState("Good");

  useEffect(() => {
    if (wavesurfer) {
      wavesurfer.registerPlugin(
        Spectrogram.create({ height: 60, splitChannels: false })
      );
      wavesurfer.on("decode", (duration) => {
        const decodedData = wavesurfer.getDecodedData();
        if (decodedData) {
          const audioData = decodedData.getChannelData(0);
          const minValue = 0.025;
          const minSilenceDuration = 0.1;
          const mergeDuration = 0.2;
          const scale = duration / audioData.length;
          const silentRegions = [];
          let start = 0;
          let end = 0;
          let isSilent = false;
          for (let i = 0; i < audioData.length; i++) {
            if (audioData[i] < minValue) {
              if (!isSilent) {
                start = i;
                isSilent = true;
              }
            } else if (isSilent) {
              end = i;
              isSilent = false;
              if (scale * (end - start) > minSilenceDuration) {
                silentRegions.push({
                  start: scale * start,
                  end: scale * end,
                });
              }
            }
          }
          const mergedRegions = [];
          let lastRegion = null;
          for (let i = 0; i < silentRegions.length; i++) {
            if (
              lastRegion &&
              silentRegions[i].start - lastRegion.end < mergeDuration
            ) {
              lastRegion.end = silentRegions[i].end;
            } else {
              lastRegion = silentRegions[i];
              mergedRegions.push(lastRegion);
            }
          }
          const regions = [];
          let lastEnd = 0;
          for (let i = 0; i < mergedRegions.length; i++) {
            regions.push({
              start: lastEnd,
              end: mergedRegions[i].start,
            });
            lastEnd = mergedRegions[i].end;
          }
          if (title === "Cough") {
            if (regions.length >= 3 && regions.length <= 6) setQuality("Good");
            else {
              setQuality("Poor");
              onPoorQuality();
            }
          } else if (title === "Voice") {
            if (regions.length !== 1) {
              setQuality("Poor");
              onPoorQuality();
            } else setQuality("Good");
          }
        }
      });
    }
    return () => {
      wavesurfer?.unAll?.();
    };
  }, [title, wavesurfer, onPoorQuality]);

  return (
    <div className="mt-4 space-y-1 bg-white">
      <h2 className="text-primary font-medium">Recorded {title} Sample</h2>
      <div className="px-4 py-4 flex items-center justify-between space-x-4 rounded-[20px] bg-[#1D1D1D] text-secondary">
        <button
          className="h-10 w-10"
          type="button"
          disabled={!isReady}
          onClick={() => {
            wavesurfer?.playPause?.();
          }}
        >
          <span className="sr-only">Play/Pause</span>
          {isPlaying ? <Pause /> : <Play />}
        </button>
        <div
          className="grow border-l-2 border-secondary"
          ref={waveformContainerRef}
        />
      </div>
      {quality === "Poor" && (
        <p className="text-primary text-xxs">
          <span className="text-error">
            We detected that the audio sample might not be accurate.
          </span>
          <br />
          <span>
            The recording doesn't seem to have the expected characteristics.
          </span>
          &nbsp;
          {title === "Voice" ? (
            <span>
              Please ensure that you're using the correct pronunciation and
              holding the sound consistently.
            </span>
          ) : (
            title === "Cough" && (
              <span>
                The recording doesn't seem to have the expected characteristics.
                Please ensure you're coughing naturally and directly into the
                microphone.
              </span>
            )
          )}
        </p>
      )}
    </div>
  );
};

const PulmonaryResult = () => {
  const { state } = useLocation();
  const [audioQualityLevel, setAudioQualityLevel] = useState(3);
  const {
    userData: { fname, lname, dob, gender, height, weight },
  } = useAuth();

  const dermaResult1 = {
    selectedArea: "face",
    image:
      "data:image/png;base64,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",
    analysis: {
      detected_conditions: [],
      overall_skin_health: "good",
      recommendations: [
        "Use sunscreen daily",
        "Stay hydrated",
        "Consider a moisturizer for dry areas",
      ],
      should_see_dermatologist: false,
      clinical_explanation:
        "The skin appears generally healthy with no major conditions detected.",
      facial_analysis: {
        spots: {
          severity: "mild",
          confidence: 0.7,
          description: "Small spots are present with mild severity.",
        },
        wrinkles: {
          severity: "none",
          confidence: 0.8,
          description: "No significant wrinkles observed.",
          areas_affected: [],
        },
        dark_circles: {
          severity: "none",
          confidence: 0.8,
          description: "No dark circles detected.",
        },
        eye_bags: {
          severity: "mild",
          confidence: 0.6,
          description: "Mild eye bags are present.",
        },
        moisture: {
          level: "normal",
          confidence: 0.75,
          description: "Skin moisture is within normal range.",
        },
        oiliness: {
          level: "normal",
          confidence: 0.7,
          description: "Oiliness is within normal levels.",
          t_zone_analysis: "Normal oiliness observed in T-zone.",
        },
        redness: {
          severity: "none",
          confidence: 0.8,
          description: "No redness detected.",
          areas_affected: [],
        },
        acne: {
          severity: "none",
          confidence: 0.8,
          description: "No visible acne present.",
          types_present: [],
        },
        pores_full_face: {
          visibility: "minimal",
          confidence: 0.7,
          description: "Pores are minimally visible.",
        },
        pores_detailed_zones: {
          t_zone: "minimal",
          cheek_area: "minimal",
          chin_area: "minimal",
        },
        firmness: {
          level: "firm",
          confidence: 0.8,
          description: "The skin appears firm.",
        },
        droopy_lower_eyelid: {
          severity: "none",
          confidence: 0.8,
          description: "No droopiness in the lower eyelid.",
        },
        droopy_upper_eyelid: {
          severity: "none",
          confidence: 0.8,
          description: "No droopiness in the upper eyelid.",
        },
        radiance: {
          level: "good",
          confidence: 0.75,
          description: "Skin radiance is good.",
        },
        tear_trough: {
          severity: "none",
          confidence: 0.8,
          description: "No significant tear trough.",
        },
      },
    },
  };

  const allergyResult = {
    data: {
      date: "2025-11-20T08:58:25.126Z",
      image_url:
        "https://files-carenowdoc-com-official-website-development.s3.amazonaws.com/careplix/AllergyAI/49a925f4-f8ef-4097-a9df-a1b605587670/f298fed3-7af3-4f59-8d39-843ac1575f19/face.jpeg",
      location: "Kolkata, India",
      facial_score: 44,
      environmental_score: 31,
      overall_risk_score: 39,
      risk_level: "Moderate",
      symptoms_detected: {
        eye_redness: false,
        eye_puffiness: true,
        nasal_inflammation: true,
        dark_circles: false,
        facial_fatigue: false,
      },
      environmental_data: {
        aqi: 162,
        pollen_count: "Moderate",
        pm2_5: 78.5,
        pm10: 80.3,
        humidity: 53,
        temperature: 27.9,
        weather_condition: "Mainly Clear",
        weather_summary:
          "The air quality is poor due to high levels of PM2.5 and PM10, which may trigger allergic symptoms.",
        location_display_name: "Kolkata, India",
      },
      facial_risk_level: "Moderate",
      environmental_risk_level: "Moderate",
      detailed_scores: {
        facial: {
          score: 4.4,
          risk_level: "Moderate",
        },
        environmental: {
          score: 3.1,
          risk_level: "Moderate",
        },
        combined: {
          score: 3.9,
          risk_level: "Moderate",
          contributors: {
            facial: {
              score: 4.4,
              risk_level: "Moderate",
            },
            environmental: {
              score: 3.1,
              risk_level: "Moderate",
            },
          },
        },
      },
      recommendation: {
        "Immediate relief measures for current symptoms":
          "Use saline nasal spray to clear nasal passages and reduce congestion. An over-the-counter antihistamine may help alleviate sneezing and itching. Apply a cool, damp cloth to the face to soothe irritation.",
        "Preventive actions based on environmental conditions":
          "Keep windows closed during high pollen days and use air conditioning with a clean air filter. Consider using a dehumidifier to control indoor humidity levels, especially in damp environments.",
        "When to seek medical attention":
          "Consult a healthcare provider if symptoms persist despite over-the-counter medication, impact your daily life, or if you experience difficulty breathing or severe sinus pressure.",
        "Lifestyle modifications to reduce exposure":
          "Shower and change clothing after spending time outdoors to remove pollen. Use protective coverings on pillows and mattresses to reduce exposure to dust mites. Keep indoor spaces clean and dry to minimize mold. Consider using allergy-proof bedding materials.",
      },
      image_analyis: {
        face_detected: true,
        reason_if_no_face: null,
        image_quality: "good",
        lighting_quality: "good",
        face_position: "good",
        visible_features: {
          eyes: false,
          nose: true,
          mouth: true,
        },
        overall_confidence: 80,
      },
      confidence_data: {
        face_detected: true,
        reason_if_no_face: null,
        image_quality: "good",
        lighting_quality: "good",
        face_position: "good",
        visible_features: {
          eyes: false,
          nose: true,
          mouth: true,
        },
        overall_confidence: 80,
      },
    },
  };

  const visionAi = {
    name: "",
    result: {
      statusCode: 200,
      scan_id: "3d74c148-3ab0-4882-a437-f2f51b234f7b",
      health_risk_score: 16,
      wellness_score: 84,
      vitals: {
        heart_rate: 84,
        oxy_sat_prcnt: 96,
        resp_rate: 18,
        bp_sys: 124,
        bp_dia: 80,
      },
      metadata: {
        device: "rppg_careplix_face_android",
        fps: 52,
        heartrate_time: [121, 162, 132, 81, 63, 61, 102, 66, 87, 124, 69, 68],
        resp_rate_time: [7.2, 16, 16, 18, 17, 18, 19, 19, 20, 19, 19, 20],
        physiological_scores: {
          height: "177.8",
          weight: "65.0",
          vo2max: "34.39",
          intensity: "Very Light",
          bmi: "20.56",
          tbw: "40.92",
          tbwp: "62.96",
          bodyfat: "14.11",
          cal_fat: "--",
          cal_carb: "--",
          bloodvolume: "4696.31",
          gender: "male",
          dob: "1998-04-25",
        },
        heart_scores: {
          HRMax: 189,
          zone_details: {
            zone: "Rest",
            lowZoneRange: "--",
            highZoneRange: 93,
          },
          HRR: "105",
          heart_utilized: "45",
          THRR: "< 104",
          rmssd: 28.81,
          sdnn: 49.92,
          pNN50_per: 40.96,
          stress_index: 0.17,
          rr_hist: [
            0, 0, 5, 10, 5, 10, 7, 6, 9, 8, 3, 8, 1, 4, 2, 2, 1, 3, 0, 0, 0, 0,
            0,
          ],
        },
        cardiovascular: {
          cardiac_out: 3.7,
          map: 94.67,
          prq: 4.67,
        },
        overall_heart_score: 65.91,
        glucose_info: {
          status: "beta",
          diabetes_control_score: 65,
          hba1c: 6.2,
        },
        mental_wellbeing_score: 88.89,
        physiological_score: 80,
      },
      notes: {
        app_code: "CPVISION",
      },
      entry_time: "2025-11-20 09:17:57.891193+00:00",
      scan_condition: null,
      user_id: "10b0f7bf-a5ec-4265-bc07-09aaf016fcb8",
      lat: null,
      long: null,
      posture: "resting",
      tz_offset: null,
    },
    eyeBlinkData: [
      {
        confidence: 0.5087591409683228,
        timestamp: -13.380000000001019,
        progress: 0,
      },
      {
        confidence: 0.5008089542388916,
        timestamp: 86.61999999999898,
        progress: 0,
      },
      {
        confidence: 0.37652477622032166,
        timestamp: 153.61999999999898,
        progress: 0,
      },
      {
        confidence: 0.3976503908634186,
        timestamp: 236.61999999999898,
        progress: 0,
      },
      {
        confidence: 0.3284142315387726,
        timestamp: 303.619999999999,
        progress: 0,
      },
      {
        confidence: 0.3341856300830841,
        timestamp: 370.619999999999,
        progress: 1,
      },
      {
        confidence: 0.277027428150177,
        timestamp: 436.619999999999,
        progress: 1,
      },
      {
        confidence: 0.1819811314344406,
        timestamp: 503.619999999999,
        progress: 1,
      },
      {
        confidence: 0.2785091996192932,
        timestamp: 570.619999999999,
        progress: 1,
      },
      {
        confidence: 0.25989261269569397,
        timestamp: 636.619999999999,
        progress: 1,
      },
      {
        confidence: 0.2541648745536804,
        timestamp: 720.619999999999,
        progress: 1,
      },
      {
        confidence: 0.21766528487205505,
        timestamp: 786.619999999999,
        progress: 1,
      },
      {
        confidence: 0.23340779542922974,
        timestamp: 853.619999999999,
        progress: 1,
      },
      {
        confidence: 0.20585235953330994,
        timestamp: 936.619999999999,
        progress: 1,
      },
      {
        confidence: 0.1858523041009903,
        timestamp: 1003.619999999999,
        progress: 2,
      },
      {
        confidence: 0.1837375909090042,
        timestamp: 1070.619999999999,
        progress: 2,
      },
      {
        confidence: 0.218035489320755,
        timestamp: 1153.619999999999,
        progress: 2,
      },
      {
        confidence: 0.23249903321266174,
        timestamp: 1236.619999999999,
        progress: 2,
      },
      {
        confidence: 0.2439923882484436,
        timestamp: 1303.619999999999,
        progress: 2,
      },
      {
        confidence: 0.21557368338108063,
        timestamp: 1403.619999999999,
        progress: 2,
      },
      {
        confidence: 0.18171662092208862,
        timestamp: 1486.619999999999,
        progress: 2,
      },
      {
        confidence: 0.21805396676063538,
        timestamp: 1553.619999999999,
        progress: 2,
      },
      {
        confidence: 0.19825038313865662,
        timestamp: 1619.619999999999,
        progress: 3,
      },
      {
        confidence: 0.19720183312892914,
        timestamp: 1703.619999999999,
        progress: 3,
      },
      {
        confidence: 0.20524738729000092,
        timestamp: 1769.619999999999,
        progress: 3,
      },
      {
        confidence: 0.16763637959957123,
        timestamp: 1853.619999999999,
        progress: 3,
      },
      {
        confidence: 0.16810283064842224,
        timestamp: 1919.619999999999,
        progress: 3,
      },
      {
        confidence: 0.17508916556835175,
        timestamp: 2003.619999999999,
        progress: 3,
      },
      {
        confidence: 0.18929561972618103,
        timestamp: 2069.619999999999,
        progress: 3,
      },
      {
        confidence: 0.17965179681777954,
        timestamp: 2136.619999999999,
        progress: 3,
      },
      {
        confidence: 0.1835242360830307,
        timestamp: 2236.619999999999,
        progress: 4,
      },
      {
        confidence: 0.15962029993534088,
        timestamp: 2286.619999999999,
        progress: 4,
      },
      {
        confidence: 0.15990717709064484,
        timestamp: 2386.619999999999,
        progress: 4,
      },
      {
        confidence: 0.16521964967250824,
        timestamp: 2453.619999999999,
        progress: 4,
      },
      {
        confidence: 0.1858055591583252,
        timestamp: 2519.619999999999,
        progress: 4,
      },
      {
        confidence: 0.18311858177185059,
        timestamp: 2603.619999999999,
        progress: 4,
      },
      {
        confidence: 0.1767389178276062,
        timestamp: 2669.619999999999,
        progress: 4,
      },
      {
        confidence: 0.22428207099437714,
        timestamp: 2753.619999999999,
        progress: 4,
      },
      {
        confidence: 0.1866358071565628,
        timestamp: 2819.619999999999,
        progress: 4,
      },
      {
        confidence: 0.1923423558473587,
        timestamp: 2903.619999999999,
        progress: 5,
      },
      {
        confidence: 0.19332008063793182,
        timestamp: 2969.619999999999,
        progress: 5,
      },
      {
        confidence: 0.1982567459344864,
        timestamp: 3036.619999999999,
        progress: 5,
      },
      {
        confidence: 0.2308109998703003,
        timestamp: 3119.619999999999,
        progress: 5,
      },
      {
        confidence: 0.1854849010705948,
        timestamp: 3186.619999999999,
        progress: 5,
      },
      {
        confidence: 0.20617446303367615,
        timestamp: 3253.619999999999,
        progress: 5,
      },
      {
        confidence: 0.2625671923160553,
        timestamp: 3303.619999999999,
        progress: 5,
      },
      {
        confidence: 0.26353558897972107,
        timestamp: 3403.619999999999,
        progress: 5,
      },
      {
        confidence: 0.2375498265028,
        timestamp: 3469.619999999999,
        progress: 6,
      },
      {
        confidence: 0.2700842320919037,
        timestamp: 3569.619999999999,
        progress: 6,
      },
      {
        confidence: 0.2842334508895874,
        timestamp: 3619.619999999999,
        progress: 6,
      },
      {
        confidence: 0.28702512383461,
        timestamp: 3703.619999999999,
        progress: 6,
      },
      {
        confidence: 0.26718828082084656,
        timestamp: 3769.619999999999,
        progress: 6,
      },
      {
        confidence: 0.29084640741348267,
        timestamp: 3836.619999999999,
        progress: 6,
      },
      {
        confidence: 0.2594531178474426,
        timestamp: 3936.619999999999,
        progress: 6,
      },
      {
        confidence: 0.2654417157173157,
        timestamp: 4019.619999999999,
        progress: 6,
      },
      {
        confidence: 0.2454676479101181,
        timestamp: 4086.619999999999,
        progress: 6,
      },
      {
        confidence: 0.21270371973514557,
        timestamp: 4152.619999999999,
        progress: 7,
      },
      {
        confidence: 0.2289007306098938,
        timestamp: 4236.619999999999,
        progress: 7,
      },
      {
        confidence: 0.21029798686504364,
        timestamp: 4302.619999999999,
        progress: 7,
      },
      {
        confidence: 0.20331937074661255,
        timestamp: 4386.619999999999,
        progress: 7,
      },
      {
        confidence: 0.20398768782615662,
        timestamp: 4452.619999999999,
        progress: 7,
      },
      {
        confidence: 0.22105610370635986,
        timestamp: 4519.619999999999,
        progress: 7,
      },
      {
        confidence: 0.2548958957195282,
        timestamp: 4602.620000000003,
        progress: 7,
      },
      {
        confidence: 0.2886311709880829,
        timestamp: 4669.620000000003,
        progress: 7,
      },
      {
        confidence: 0.2944847345352173,
        timestamp: 4736.620000000003,
        progress: 8,
      },
      {
        confidence: 0.2996363341808319,
        timestamp: 4819.620000000003,
        progress: 8,
      },
      {
        confidence: 0.28550511598587036,
        timestamp: 4886.620000000003,
        progress: 8,
      },
      {
        confidence: 0.286702424287796,
        timestamp: 4952.620000000003,
        progress: 8,
      },
      {
        confidence: 0.30049431324005127,
        timestamp: 5019.620000000003,
        progress: 8,
      },
      {
        confidence: 0.2906407415866852,
        timestamp: 5102.620000000003,
        progress: 8,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5169.620000000003,
        progress: 8,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5252.620000000003,
        progress: 8,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5269.620000000003,
        progress: 8,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5286.620000000003,
        progress: 8,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5302.620000000003,
        progress: 8,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5319.620000000003,
        progress: 8,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5336.620000000003,
        progress: 8,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5352.620000000003,
        progress: 8,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5369.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5386.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5402.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5419.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5436.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5452.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5469.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5486.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5502.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5519.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5536.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5552.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5569.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5586.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5602.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5619.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5636.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5652.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5669.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5686.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5702.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5719.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5736.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5752.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5769.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5786.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5802.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5819.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5836.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5852.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5869.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5886.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5902.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5919.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5936.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5952.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5969.620000000003,
        progress: 9,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 5986.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6002.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6019.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6036.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6052.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6069.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6086.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6102.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6119.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6152.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6169.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6185.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6202.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6219.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6235.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6252.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6269.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6285.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6302.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6319.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6335.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6352.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6369.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6385.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6402.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6419.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6435.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6452.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6469.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6485.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6502.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6519.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6535.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6552.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6569.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6602.620000000003,
        progress: 10,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6619.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6635.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6652.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6685.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6702.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6719.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6752.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6785.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6802.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6819.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6835.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6852.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6885.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6902.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6919.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6935.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6952.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 6985.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7002.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7019.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7035.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7052.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7069.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7102.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7119.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7135.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7152.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7169.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7185.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7219.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7235.620000000003,
        progress: 11,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7252.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7269.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7302.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7319.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7335.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7352.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7369.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7385.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7402.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7419.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7435.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7452.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7469.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7485.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7519.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7535.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7552.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7569.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7585.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7602.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7619.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7635.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7652.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7669.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7702.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7719.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7735.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7752.620000000003,
        progress: 12,
      },
      {
        confidence: 0.26013562083244324,
        timestamp: 7769.620000000003,
        progress: 12,
      },
      {
        confidence: 0.1948506087064743,
        timestamp: 7785.620000000003,
        progress: 12,
      },
      {
        confidence: 0.18310628831386566,
        timestamp: 7852.620000000003,
        progress: 12,
      },
      {
        confidence: 0.1774677187204361,
        timestamp: 7919.620000000003,
        progress: 13,
      },
      {
        confidence: 0.17000706493854523,
        timestamp: 7985.620000000003,
        progress: 13,
      },
      {
        confidence: 0.1594720035791397,
        timestamp: 8052.620000000003,
        progress: 13,
      },
      {
        confidence: 0.15315333008766174,
        timestamp: 8119.620000000003,
        progress: 13,
      },
      {
        confidence: 0.15503427386283875,
        timestamp: 8185.620000000003,
        progress: 13,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8269.620000000003,
        progress: 13,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8335.620000000003,
        progress: 13,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8352.620000000003,
        progress: 13,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8369.620000000003,
        progress: 13,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8385.620000000003,
        progress: 13,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8402.620000000003,
        progress: 13,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8418.620000000003,
        progress: 13,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8435.620000000003,
        progress: 13,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8452.620000000003,
        progress: 13,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8468.620000000003,
        progress: 13,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8485.620000000003,
        progress: 13,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8502.620000000003,
        progress: 13,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8518.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8535.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8552.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8568.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8585.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8602.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8618.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8635.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8652.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8668.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8685.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8702.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8718.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8735.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8752.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8768.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8785.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8802.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8818.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8835.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8852.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8868.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8885.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8902.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8918.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8935.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8952.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8968.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 8985.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9002.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9018.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9035.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9052.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9068.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9085.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9102.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9118.620000000003,
        progress: 14,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9135.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9152.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9168.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9185.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9202.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9218.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9235.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9252.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9268.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9285.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9302.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9318.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9335.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9352.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9368.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9385.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9402.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9418.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9435.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9452.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9468.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9485.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9502.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9518.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9535.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9552.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9568.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9585.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9602.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9618.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9635.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9652.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9668.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9685.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9702.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9718.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9735.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9752.620000000003,
        progress: 15,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9768.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9785.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9802.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9818.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9835.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9852.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9868.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9885.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9902.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9918.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9935.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9952.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9968.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 9985.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10002.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10018.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10035.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10052.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10068.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10085.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10102.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10118.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10135.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10152.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10168.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10185.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10202.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10218.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10235.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10252.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10268.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10285.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10302.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10318.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10335.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10352.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10368.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10385.620000000003,
        progress: 16,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10402.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10418.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10435.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10452.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10468.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10485.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10502.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10518.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10535.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10552.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10568.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10585.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10602.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10618.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10635.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1494072824716568,
        timestamp: 10652.620000000003,
        progress: 17,
      },
      {
        confidence: 0.16671046614646912,
        timestamp: 10668.620000000003,
        progress: 17,
      },
      {
        confidence: 0.1680142879486084,
        timestamp: 10735.620000000003,
        progress: 17,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 10818.620000000003,
        progress: 17,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 10901.620000000003,
        progress: 17,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 10918.620000000003,
        progress: 17,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 10935.620000000003,
        progress: 17,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 10951.620000000003,
        progress: 17,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 10968.620000000003,
        progress: 17,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 10985.620000000003,
        progress: 17,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11001.620000000003,
        progress: 17,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11018.620000000003,
        progress: 17,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11035.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11051.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11068.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11085.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11101.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11118.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11135.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11151.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11168.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11185.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11201.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11218.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11235.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11251.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11268.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11285.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11301.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11318.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11335.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11351.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11368.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11385.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11401.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11418.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11435.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11451.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11468.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11485.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11501.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11518.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11535.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11551.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11568.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11585.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11601.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11618.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11635.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11651.620000000003,
        progress: 18,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11668.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11685.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11701.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11718.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11735.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11751.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11768.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11785.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11801.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11818.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11835.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11851.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11868.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11885.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11901.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11918.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11935.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11951.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11968.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 11985.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12001.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12018.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12035.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12051.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12068.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12085.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12101.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12118.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12135.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12151.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12168.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12185.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12201.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12218.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12235.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12251.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12268.620000000003,
        progress: 19,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12285.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12301.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12318.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12335.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12351.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12368.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12385.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12401.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12418.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12435.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12451.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12468.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12485.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12501.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12518.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12535.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12551.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12568.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12585.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12601.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12618.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12635.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12651.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12668.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12685.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12701.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12718.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12735.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12751.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12768.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12785.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12801.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12818.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12835.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12851.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12868.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12885.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12901.620000000003,
        progress: 20,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12918.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12935.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12951.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12968.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 12985.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 13001.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 13018.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 13034.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 13051.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 13068.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 13084.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 13101.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 13118.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 13134.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 13151.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 13168.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 13184.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 13201.620000000003,
        progress: 21,
      },
      {
        confidence: 0.15006907284259796,
        timestamp: 13218.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13234.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13318.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13334.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13351.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13368.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13384.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13401.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13418.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13434.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13451.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13468.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13484.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13501.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13518.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13534.620000000003,
        progress: 21,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13551.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13568.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13584.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13601.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13618.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13634.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13651.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13668.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13684.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13701.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13718.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13734.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13751.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13768.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13784.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13801.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13818.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13834.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13851.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13868.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13884.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13901.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13918.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13934.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13951.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13968.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 13984.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14001.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14018.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14034.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14051.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14068.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14084.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14101.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14118.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14134.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14151.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14168.620000000003,
        progress: 22,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14184.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14201.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14218.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14234.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14251.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14268.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14284.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14301.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14318.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14334.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14351.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14368.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14384.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14401.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14418.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14434.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14451.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14468.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14484.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14501.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14518.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14534.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14551.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14568.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14584.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14601.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14618.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14634.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14651.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14668.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14684.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14701.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14718.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14734.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14751.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14768.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14784.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14801.620000000003,
        progress: 23,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14818.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14834.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14851.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14868.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14884.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14901.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14918.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14934.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14951.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14968.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 14984.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15001.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15018.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15034.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15051.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15068.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15084.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15101.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15118.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15134.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15151.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15168.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15184.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15201.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15218.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15234.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15251.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15268.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15284.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15301.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15318.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15334.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15351.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15367.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15384.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15401.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15417.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15434.620000000003,
        progress: 24,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15451.620000000003,
        progress: 25,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15467.620000000003,
        progress: 25,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15484.620000000003,
        progress: 25,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15501.620000000003,
        progress: 25,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15517.620000000003,
        progress: 25,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15534.620000000003,
        progress: 25,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15551.620000000003,
        progress: 25,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15567.620000000003,
        progress: 25,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15584.620000000003,
        progress: 25,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15601.620000000003,
        progress: 25,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15617.620000000003,
        progress: 25,
      },
      {
        confidence: 0.1732347309589386,
        timestamp: 15634.620000000003,
        progress: 25,
      },
      {
        confidence: 0.16836436092853546,
        timestamp: 15651.620000000003,
        progress: 25,
      },
      {
        confidence: 0.20283713936805725,
        timestamp: 15717.620000000003,
        progress: 25,
      },
      {
        confidence: 0.20923744142055511,
        timestamp: 15784.620000000003,
        progress: 25,
      },
      {
        confidence: 0.24758219718933105,
        timestamp: 15851.620000000003,
        progress: 25,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 15934.620000000003,
        progress: 25,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16017.620000000003,
        progress: 25,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16034.620000000003,
        progress: 25,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16051.620000000003,
        progress: 25,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16067.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16084.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16101.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16117.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16134.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16151.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16167.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16184.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16201.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16217.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16234.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16251.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16267.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16284.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16301.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16317.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16334.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16351.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16367.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16384.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16401.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16417.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16434.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16451.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16467.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16484.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16501.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16517.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16534.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16551.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16567.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16584.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16601.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16617.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16634.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16651.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16667.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16684.620000000003,
        progress: 26,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16701.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16717.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16734.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16751.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16767.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16784.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16801.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16817.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16834.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16851.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16867.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16884.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16901.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16917.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16934.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16951.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16967.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 16984.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17001.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17017.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17034.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17051.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17067.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17084.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17101.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17117.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17134.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17151.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17167.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17184.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17201.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17217.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17234.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17251.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17267.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17284.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17301.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17317.620000000003,
        progress: 27,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17334.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17351.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17367.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17384.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17401.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17417.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17434.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17451.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17467.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17484.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17501.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17517.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17534.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17551.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17567.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17584.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17601.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17617.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17634.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17651.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17667.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17684.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17700.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17717.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17734.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17750.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17767.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17784.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17800.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17817.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17834.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17850.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17867.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17884.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17917.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17934.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17950.620000000003,
        progress: 28,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17967.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 17984.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18000.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18017.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18034.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18050.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18067.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18084.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18100.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18117.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18134.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18150.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18167.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18184.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18200.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18217.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18234.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18250.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18267.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18284.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18300.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18317.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18334.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22246478497982025,
        timestamp: 18350.620000000003,
        progress: 29,
      },
      {
        confidence: 0.1813458949327469,
        timestamp: 18367.620000000003,
        progress: 29,
      },
      {
        confidence: 0.19288931787014008,
        timestamp: 18434.620000000003,
        progress: 29,
      },
      {
        confidence: 0.22848737239837646,
        timestamp: 18500.620000000003,
        progress: 29,
      },
      {
        confidence: 0.25755423307418823,
        timestamp: 18584.620000000003,
        progress: 29,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18650.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18734.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18750.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18767.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18784.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18800.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18817.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18834.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18850.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18867.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18884.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18900.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18917.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18934.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18950.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18967.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 18984.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19000.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19017.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19034.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19050.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19067.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19084.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19100.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19117.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19134.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19150.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19167.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19184.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19200.620000000003,
        progress: 30,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19217.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19234.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19250.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19267.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19284.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19317.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19350.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19367.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19400.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19417.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19434.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19450.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19467.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19484.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19500.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19517.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19534.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19550.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19567.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19584.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19600.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19617.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19634.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19650.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19667.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19684.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19700.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19717.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19734.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19750.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19767.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19784.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19800.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19817.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19834.620000000003,
        progress: 31,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19850.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19867.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19884.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19900.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19917.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19934.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19950.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19967.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 19984.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20000.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20017.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20034.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20050.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20067.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20083.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20100.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20117.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20133.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20150.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20167.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20183.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20200.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20217.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20233.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20250.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20267.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20283.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20300.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20317.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20333.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20350.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20367.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20383.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20400.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20417.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20433.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20450.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20467.620000000003,
        progress: 32,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20483.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20500.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20517.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20533.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20550.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20567.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20583.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20600.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20617.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20633.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20650.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20667.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20683.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20700.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20717.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20733.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20750.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20767.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20783.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20800.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20817.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20833.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20850.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20867.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20883.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20900.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20917.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20933.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20950.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20967.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 20983.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 21000.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 21017.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 21033.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 21050.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 21067.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 21083.620000000003,
        progress: 33,
      },
      {
        confidence: 0.2284676432609558,
        timestamp: 21100.620000000003,
        progress: 33,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21117.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21200.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21217.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21233.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21250.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21267.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21283.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21300.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21317.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21333.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21350.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21367.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21383.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21400.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21417.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21433.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21450.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21467.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21483.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21500.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21517.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21533.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21550.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21567.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21583.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21600.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21617.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21633.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21650.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21667.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21683.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21700.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21717.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21733.620000000003,
        progress: 34,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21750.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21767.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21783.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21800.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21817.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21833.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21850.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21867.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21883.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21900.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21917.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21933.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21950.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21967.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 21983.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22000.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22017.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22033.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22050.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22067.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22083.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22100.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22117.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22133.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22150.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22167.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22183.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22200.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22217.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22233.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22250.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22267.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22283.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22300.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22317.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22333.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22350.620000000003,
        progress: 35,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22367.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22383.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22400.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22416.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22433.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22450.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22466.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22483.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22500.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22516.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22533.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22550.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22566.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22583.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22600.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22616.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22633.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22650.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22666.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22683.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22700.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22716.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22733.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22750.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22766.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22783.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22800.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22816.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22833.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22850.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22866.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22883.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22900.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22916.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22933.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22950.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22966.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 22983.620000000003,
        progress: 36,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23000.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23016.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23033.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23050.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23066.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23083.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23100.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23116.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23133.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23150.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23166.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23183.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23200.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23216.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23233.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23250.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23266.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23283.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23300.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23316.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23333.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23350.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23366.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23383.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23400.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23416.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23433.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23450.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23466.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23483.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23500.620000000003,
        progress: 37,
      },
      {
        confidence: 0.1739388108253479,
        timestamp: 23516.620000000003,
        progress: 37,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23533.620000000003,
        progress: 37,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23616.620000000003,
        progress: 37,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23633.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23650.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23666.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23683.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23700.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23716.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23733.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23750.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23766.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23783.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23800.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23816.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23833.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23850.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23866.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23883.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23900.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23916.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23933.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23950.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23966.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 23983.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24000.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24016.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24033.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24050.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24066.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24083.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24100.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24116.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24133.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24150.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24166.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24183.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24200.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24216.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24233.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24250.620000000003,
        progress: 38,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24266.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24283.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24300.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24316.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24333.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24350.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24366.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24383.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24400.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24416.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24433.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24450.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24466.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24483.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24500.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24516.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24533.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24550.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24566.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24583.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24600.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24616.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24633.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24650.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24666.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24683.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24699.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24716.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24733.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24749.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24766.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24783.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24799.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24816.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24833.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24849.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24866.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24883.620000000003,
        progress: 39,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24899.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24916.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24933.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24949.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24966.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24983.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 24999.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25016.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25033.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25049.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25066.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25083.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25099.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25116.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25133.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25149.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25166.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25183.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25199.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25216.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25233.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25249.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25266.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25283.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25299.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25316.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25333.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25349.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25366.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25383.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25399.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25416.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25433.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25449.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25466.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25483.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25499.620000000003,
        progress: 40,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25516.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25533.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25549.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25566.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25583.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25599.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25616.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25633.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25649.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25666.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25683.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25699.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25716.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25733.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25749.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25766.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25783.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25799.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25816.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25833.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25849.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25866.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25883.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25899.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25916.620000000003,
        progress: 41,
      },
      {
        confidence: 0.24745622277259827,
        timestamp: 25933.620000000003,
        progress: 41,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 25949.620000000003,
        progress: 41,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26033.620000000003,
        progress: 41,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26049.620000000003,
        progress: 41,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26066.620000000003,
        progress: 41,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26083.620000000003,
        progress: 41,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26099.620000000003,
        progress: 41,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26116.620000000003,
        progress: 41,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26133.620000000003,
        progress: 41,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26149.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26166.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26183.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26199.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26216.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26233.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26249.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26266.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26283.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26299.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26316.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26333.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26349.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26366.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26383.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26399.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26416.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26433.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26449.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26466.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26483.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26499.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26516.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26533.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26549.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26566.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26583.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26599.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26616.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26633.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26649.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26666.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26683.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26699.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26716.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26733.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26749.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26766.620000000003,
        progress: 42,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26783.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26799.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26816.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26833.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26849.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26866.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26883.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26899.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26916.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26933.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26949.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26966.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26983.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 26999.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27016.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27032.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27049.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27066.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27082.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27099.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27116.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27132.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27149.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27166.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27182.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27199.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27216.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27232.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27249.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27266.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27282.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27299.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27316.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27332.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27349.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27366.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27382.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27399.620000000003,
        progress: 43,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27416.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27432.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27449.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27466.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27482.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27499.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27516.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27532.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27549.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27566.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27582.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27599.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27616.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27632.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27649.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27666.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27682.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27699.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27716.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27732.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27749.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27766.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27782.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27799.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27816.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27832.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27849.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27866.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27882.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27899.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27916.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27932.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27949.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27966.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27982.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 27999.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28016.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28032.620000000003,
        progress: 44,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28049.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28066.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28082.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28099.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28116.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28132.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28149.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28166.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28182.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28199.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28216.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28232.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28249.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28266.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28282.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28299.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28316.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28332.620000000003,
        progress: 45,
      },
      {
        confidence: 0.27808699011802673,
        timestamp: 28349.620000000003,
        progress: 45,
      },
      {
        confidence: 0.22438816726207733,
        timestamp: 28366.620000000003,
        progress: 45,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28432.620000000003,
        progress: 45,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28516.620000000003,
        progress: 45,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28532.620000000003,
        progress: 45,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28549.620000000003,
        progress: 45,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28566.620000000003,
        progress: 45,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28582.620000000003,
        progress: 45,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28599.620000000003,
        progress: 45,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28616.620000000003,
        progress: 45,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28632.620000000003,
        progress: 45,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28649.620000000003,
        progress: 45,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28666.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28682.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28699.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28716.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28732.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28749.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28766.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28782.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28799.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28816.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28832.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28849.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28866.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28882.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28899.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28916.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28932.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28949.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28966.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28982.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 28999.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29016.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29032.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29049.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29066.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29082.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29099.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29116.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29132.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29149.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29166.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29182.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29199.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29216.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29232.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29249.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29266.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29282.620000000003,
        progress: 46,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29299.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29316.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29332.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29349.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29365.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29382.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29399.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29415.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29432.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29449.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29465.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29482.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29499.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29515.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29532.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29549.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29565.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29582.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29599.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29615.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29632.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29649.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29665.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29682.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29699.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29715.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29732.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29749.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29765.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29782.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29799.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29815.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29832.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29849.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29865.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29882.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29899.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29915.620000000003,
        progress: 47,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29932.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29949.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29982.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 29999.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30015.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30032.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30049.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30065.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30082.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30099.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30115.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30132.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30149.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30165.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30182.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30199.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30215.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30232.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30249.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30265.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30282.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30299.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30315.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30332.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30349.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30365.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30382.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30399.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30415.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30432.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30449.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30465.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30482.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30499.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30515.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30532.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30549.620000000003,
        progress: 48,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30565.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30582.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30599.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30615.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30632.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30649.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30665.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30682.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30699.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30715.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30732.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30749.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30765.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30782.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30799.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30815.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30832.620000000003,
        progress: 49,
      },
      {
        confidence: 0.23820343613624573,
        timestamp: 30849.620000000003,
        progress: 49,
      },
      {
        confidence: 0.3387320935726166,
        timestamp: 30865.620000000003,
        progress: 49,
      },
      {
        confidence: 0.3369669020175934,
        timestamp: 30932.620000000003,
        progress: 49,
      },
      {
        confidence: 0.33636829257011414,
        timestamp: 30999.620000000003,
        progress: 49,
      },
      {
        confidence: 0.3386521637439728,
        timestamp: 31065.620000000003,
        progress: 49,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31149.620000000003,
        progress: 49,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31215.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31232.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31249.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31265.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31282.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31299.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31315.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31332.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31349.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31365.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31382.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31399.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31415.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31432.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31449.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31465.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31482.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31499.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31515.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31532.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31549.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31565.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31582.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31599.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31615.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31632.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31648.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31665.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31682.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31698.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31715.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31732.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31748.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31765.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31782.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31798.620000000003,
        progress: 50,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31815.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31832.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31848.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31865.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31882.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31898.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31915.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31932.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31948.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31965.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31982.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 31998.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32015.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32032.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32048.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32065.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32082.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32098.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32115.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32132.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32148.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32165.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32182.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32198.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32215.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32232.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32248.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32265.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32282.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32298.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32315.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32332.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32348.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32365.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32382.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32398.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32415.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32432.620000000003,
        progress: 51,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32448.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32465.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32482.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32498.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32515.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32532.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32548.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32565.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32582.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32598.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32615.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32632.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32648.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32665.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32682.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32698.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32715.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32732.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32748.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32765.620000000003,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32782.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32798.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32815.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32832.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32848.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32865.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32882.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32898.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32915.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32932.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32948.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32965.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32982.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 32998.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33015.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33032.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33048.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33065.62,
        progress: 52,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33082.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33098.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33115.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33132.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33148.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33165.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33182.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33198.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33232.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33248.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33265.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33282.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33298.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33315.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33332.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33348.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33365.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33382.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33398.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33415.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33432.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33448.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33465.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33482.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33498.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33515.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33532.62,
        progress: 53,
      },
      {
        confidence: 0.27779337763786316,
        timestamp: 33548.62,
        progress: 53,
      },
      {
        confidence: 0.3355099558830261,
        timestamp: 33565.62,
        progress: 53,
      },
      {
        confidence: 0.34286320209503174,
        timestamp: 33632.62,
        progress: 53,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33698.62,
        progress: 53,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33765.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33782.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33798.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33815.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33832.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33848.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33865.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33882.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33898.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33915.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33932.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33948.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33965.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33981.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 33998.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34015.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34031.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34048.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34065.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34081.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34098.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34115.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34131.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34148.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34165.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34181.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34198.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34215.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34231.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34248.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34265.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34281.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34298.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34315.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34331.62,
        progress: 54,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34348.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34365.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34381.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34398.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34415.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34431.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34448.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34465.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34481.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34498.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34515.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34531.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34548.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34565.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34581.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34598.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34615.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34631.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34648.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34665.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34681.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34698.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34715.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34731.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34748.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34765.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34781.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34798.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34815.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34831.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34848.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34865.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34881.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34898.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34915.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34931.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34948.62,
        progress: 55,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34965.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34981.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 34998.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35015.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35031.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35048.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35065.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35081.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35098.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35115.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35131.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35148.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35165.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35181.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35198.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35215.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35231.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35248.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35265.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35281.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35298.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35315.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35331.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35348.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35365.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35381.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35398.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35415.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35431.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35448.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35465.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35481.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35498.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35515.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35531.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35548.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35565.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35581.62,
        progress: 56,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35598.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35615.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35631.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35648.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35665.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35681.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35698.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35715.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35731.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35748.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35765.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35835.94,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35853.82,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35881.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35898.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35915.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35931.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35948.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35965.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35981.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 35998.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 36015.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 36031.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 36048.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 36065.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 36081.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 36098.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 36115.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 36131.62,
        progress: 57,
      },
      {
        confidence: 0.32391074299812317,
        timestamp: 36148.62,
        progress: 57,
      },
      {
        confidence: 0.29283180832862854,
        timestamp: 36165.62,
        progress: 57,
      },
      {
        confidence: 0.32140934467315674,
        timestamp: 36231.62,
        progress: 58,
      },
      {
        confidence: 0.3295954167842865,
        timestamp: 36314.62,
        progress: 58,
      },
      {
        confidence: 0.3585520386695862,
        timestamp: 36381.62,
        progress: 58,
      },
      {
        confidence: 0.3534611463546753,
        timestamp: 36448.62,
        progress: 58,
      },
      {
        confidence: 0.36411112546920776,
        timestamp: 36514.62,
        progress: 58,
      },
      {
        confidence: 0.3709368407726288,
        timestamp: 36581.62,
        progress: 58,
      },
      {
        confidence: 0.38266855478286743,
        timestamp: 36664.62,
        progress: 58,
      },
      {
        confidence: 0.3656243085861206,
        timestamp: 36731.62,
        progress: 58,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 36814.62,
        progress: 58,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 36914.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 36931.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 36948.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 36964.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 36981.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 36998.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37014.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37031.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37048.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37064.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37081.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37098.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37114.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37131.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37148.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37164.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37181.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37198.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37214.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37231.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37248.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37264.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37281.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37298.62,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37314.619999999995,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37331.619999999995,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37348.619999999995,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37364.619999999995,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37381.619999999995,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37398.619999999995,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37414.619999999995,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37431.619999999995,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37448.619999999995,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37464.619999999995,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37481.619999999995,
        progress: 59,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37498.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37514.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37531.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37548.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37564.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37581.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37598.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37614.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37631.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37648.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37664.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37681.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37698.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37714.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37731.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37748.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37764.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37781.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37798.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37814.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37831.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37848.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37864.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37881.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37898.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37914.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37931.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37948.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37964.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37981.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 37998.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38014.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38031.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38048.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38064.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38081.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38098.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38114.619999999995,
        progress: 60,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38131.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38148.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38164.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38181.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38198.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38214.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38231.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38248.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38264.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38281.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38298.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38314.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38331.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38348.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38364.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38381.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38398.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38414.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38431.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38448.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38464.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38481.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38498.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38514.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38531.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38548.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38564.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38581.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38598.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38614.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38631.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38647.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38664.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38681.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38697.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38714.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38731.619999999995,
        progress: 61,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38747.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38764.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38781.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38797.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38814.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38831.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38847.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38864.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38881.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38897.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38914.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38931.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38947.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38964.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38981.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 38997.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 39014.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 39031.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 39047.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 39064.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 39081.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 39097.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 39114.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 39131.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 39147.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 39164.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 39181.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 39197.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 39214.619999999995,
        progress: 62,
      },
      {
        confidence: 0.351246178150177,
        timestamp: 39231.619999999995,
        progress: 62,
      },
      {
        confidence: 0.30277055501937866,
        timestamp: 39247.619999999995,
        progress: 62,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39314.619999999995,
        progress: 62,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39414.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39431.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39447.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39464.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39481.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39497.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39514.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39531.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39547.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39564.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39581.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39597.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39614.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39631.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39647.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39664.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39681.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39697.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39714.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39731.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39747.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39764.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39781.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39797.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39814.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39831.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39847.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39864.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39881.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39897.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39914.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39931.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39947.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39964.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39981.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 39997.619999999995,
        progress: 63,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40014.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40031.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40047.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40064.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40081.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40097.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40114.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40131.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40147.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40164.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40181.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40197.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40214.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40231.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40247.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40264.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40281.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40297.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40314.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40331.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40347.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40364.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40381.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40397.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40414.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40431.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40447.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40464.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40481.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40497.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40514.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40531.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40547.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40564.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40581.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40597.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40614.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40631.619999999995,
        progress: 64,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40647.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40664.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40681.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40697.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40714.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40731.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40747.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40764.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40781.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40797.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40814.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40831.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40847.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40864.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40881.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40897.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40914.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40930.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40947.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40964.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40980.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 40997.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41014.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41030.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41047.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41064.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41080.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41097.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41114.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41130.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41147.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41164.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41180.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41197.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41214.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41230.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41247.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41264.619999999995,
        progress: 65,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41280.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41297.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41314.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41330.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41347.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41364.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41380.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41397.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41414.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41430.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41447.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41464.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41480.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41497.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41514.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41530.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41547.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41564.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41580.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41597.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41614.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41630.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41647.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41664.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41680.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41697.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41714.619999999995,
        progress: 66,
      },
      {
        confidence: 0.33598482608795166,
        timestamp: 41730.619999999995,
        progress: 66,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 41747.619999999995,
        progress: 66,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 41830.619999999995,
        progress: 66,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 41847.619999999995,
        progress: 66,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 41864.619999999995,
        progress: 66,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 41880.619999999995,
        progress: 66,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 41897.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 41914.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 41930.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 41947.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 41964.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 41980.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 41997.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42014.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42030.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42047.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42064.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42080.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42097.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42114.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42130.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42147.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42164.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42180.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42197.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42214.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42230.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42247.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42264.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42280.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42297.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42314.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42330.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42347.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42364.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42380.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42397.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42414.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42430.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42447.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42464.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42480.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42497.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42514.619999999995,
        progress: 67,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42530.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42547.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42564.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42580.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42597.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42614.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42630.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42647.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42664.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42680.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42697.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42714.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42730.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42747.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42764.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42780.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42797.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42814.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42830.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42847.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42864.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42880.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42897.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42914.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42930.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42947.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42964.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42980.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 42997.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43014.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43030.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43047.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43064.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43080.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43097.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43114.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43130.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43147.619999999995,
        progress: 68,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43164.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43180.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43197.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43214.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43230.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43247.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43264.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43280.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43297.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43313.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43330.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43347.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43363.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43380.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43397.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43413.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43430.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43447.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43463.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43480.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43497.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43513.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43530.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43547.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43563.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43580.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43597.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43613.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43630.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43647.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43663.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43680.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43697.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43713.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43730.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43747.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43763.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43780.619999999995,
        progress: 69,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43797.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43813.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43830.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43847.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43863.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43880.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43897.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43913.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43930.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43947.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43963.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43980.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 43997.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 44013.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 44030.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 44047.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 44063.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 44080.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 44097.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 44113.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 44130.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3037877380847931,
        timestamp: 44147.619999999995,
        progress: 70,
      },
      {
        confidence: 0.28939419984817505,
        timestamp: 44163.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3149586319923401,
        timestamp: 44230.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3468356132507324,
        timestamp: 44297.619999999995,
        progress: 70,
      },
      {
        confidence: 0.32631057500839233,
        timestamp: 44380.619999999995,
        progress: 70,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44447.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44513.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44547.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44563.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44580.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44597.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44613.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44630.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44647.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44663.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44680.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44697.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44713.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44730.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44747.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44763.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44780.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44797.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44813.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44830.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44847.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44863.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44880.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44897.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44913.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44930.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44947.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44963.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44980.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 44997.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45013.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45030.619999999995,
        progress: 71,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45047.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45063.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45080.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45097.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45113.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45130.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45147.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45163.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45180.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45197.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45213.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45230.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45247.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45263.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45280.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45297.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45313.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45330.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45347.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45363.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45380.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45397.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45413.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45430.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45447.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45463.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45480.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45497.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45513.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45530.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45547.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45563.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45580.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45596.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45613.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45630.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45646.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45663.619999999995,
        progress: 72,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45680.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45696.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45713.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45730.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45746.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45763.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45780.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45796.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45813.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45830.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45846.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45863.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45880.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45896.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45913.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45930.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45946.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45963.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45980.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 45996.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46013.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46030.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46046.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46063.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46080.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46096.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46113.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46130.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46146.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46163.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46180.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46196.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46213.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46230.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46246.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46263.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46280.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46296.619999999995,
        progress: 73,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46313.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46330.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46346.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46363.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46380.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46396.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46413.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46430.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46446.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46463.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46480.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46496.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46513.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46530.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46546.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46563.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46580.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46596.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46613.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46630.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46646.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46663.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46680.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46696.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46713.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46730.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46746.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46763.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46780.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46796.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46813.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46830.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3102057874202728,
        timestamp: 46846.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3449860215187073,
        timestamp: 46863.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3065069615840912,
        timestamp: 46930.619999999995,
        progress: 74,
      },
      {
        confidence: 0.3087838590145111,
        timestamp: 46996.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47080.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47163.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47180.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47196.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47213.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47230.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47246.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47263.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47280.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47296.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47313.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47330.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47346.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47363.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47380.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47396.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47413.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47430.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47446.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47463.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47480.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47496.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47513.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47530.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47546.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47563.619999999995,
        progress: 75,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47580.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47596.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47613.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47630.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47646.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47663.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47680.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47696.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47713.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47730.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47746.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47763.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47780.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47796.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47813.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47830.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47846.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47863.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47880.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47896.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47913.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47930.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47946.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47963.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47979.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 47996.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48013.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48029.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48046.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48063.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48079.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48096.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48113.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48129.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48146.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48163.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48179.619999999995,
        progress: 76,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48196.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48213.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48229.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48246.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48263.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48279.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48296.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48313.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48329.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48346.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48363.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48379.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48396.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48413.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48429.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48446.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48463.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48479.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48496.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48513.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48529.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48546.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48563.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48579.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48596.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48613.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48629.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48646.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48663.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48679.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48696.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48713.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48729.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48746.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48763.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48779.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48796.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48813.619999999995,
        progress: 77,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48829.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48846.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48863.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48879.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48896.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48913.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48929.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48946.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48963.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48979.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 48996.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49013.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49029.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49046.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49063.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49079.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49096.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49113.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49129.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49146.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49163.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49179.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49196.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49213.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49229.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49246.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49263.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49279.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49296.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49313.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49329.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49346.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49363.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49379.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49396.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49413.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49429.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49446.619999999995,
        progress: 78,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49463.619999999995,
        progress: 79,
      },
      {
        confidence: 0.34620606899261475,
        timestamp: 49479.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49496.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49579.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49596.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49613.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49629.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49646.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49663.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49679.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49696.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49713.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49729.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49746.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49763.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49779.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49796.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49813.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49829.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49846.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49863.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49879.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49896.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49913.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49929.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49946.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49963.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49979.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 49996.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50013.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50029.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50046.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50063.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50079.619999999995,
        progress: 79,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50096.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50113.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50129.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50146.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50163.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50179.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50196.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50212.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50229.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50246.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50262.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50279.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50296.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50312.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50329.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50346.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50362.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50379.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50396.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50412.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50429.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50446.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50462.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50479.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50496.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50512.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50529.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50546.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50562.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50579.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50596.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50612.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50629.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50646.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50662.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50679.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50696.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50712.619999999995,
        progress: 80,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50729.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50746.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50762.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50779.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50796.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50812.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50829.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50846.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50862.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50879.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50896.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50912.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50929.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50946.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50962.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50979.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 50996.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51012.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51029.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51046.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51062.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51079.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51096.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51112.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51129.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51146.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51162.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51179.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51196.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51212.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51229.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51246.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51262.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51279.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51296.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51312.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51329.619999999995,
        progress: 81,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51346.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51362.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51379.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51396.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51412.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51429.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51446.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51462.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51479.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51496.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51512.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51529.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51546.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51562.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51579.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51596.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51612.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51629.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51646.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51662.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51679.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51696.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51712.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51729.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51746.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51762.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51779.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51796.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51812.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51829.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51846.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51862.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51879.619999999995,
        progress: 82,
      },
      {
        confidence: 0.060762107372283936,
        timestamp: 51896.619999999995,
        progress: 82,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 51912.619999999995,
        progress: 82,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 51996.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52012.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52029.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52046.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52062.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52079.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52096.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52112.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52129.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52146.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52162.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52179.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52196.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52212.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52229.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52246.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52262.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52279.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52296.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52312.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52329.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52346.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52362.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52379.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52396.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52412.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52429.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52446.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52462.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52479.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52495.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52512.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52529.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52545.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52562.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52579.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52595.619999999995,
        progress: 83,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52612.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52629.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52645.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52662.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52679.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52695.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52712.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52729.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52745.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52762.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52779.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52795.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52812.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52829.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52845.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52862.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52879.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52895.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52912.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52929.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52945.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52962.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52979.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 52995.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53012.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53029.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53045.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53062.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53079.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53095.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53112.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53129.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53145.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53162.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53179.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53195.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53212.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53229.619999999995,
        progress: 84,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53245.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53262.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53279.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53295.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53312.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53329.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53345.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53362.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53379.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53395.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53412.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53429.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53445.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53462.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53479.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53495.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53512.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53529.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53545.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53562.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53579.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53595.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53612.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53629.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53645.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53662.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53679.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53695.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53712.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53729.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53745.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53762.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53779.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53795.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53812.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53829.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53845.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53862.619999999995,
        progress: 85,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53879.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53895.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53912.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53929.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53945.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53962.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53979.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 53995.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54012.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54029.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54045.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54062.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54079.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54095.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54112.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54129.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54145.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54162.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54179.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54195.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54212.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54229.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54245.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54262.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54279.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54295.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33007434010505676,
        timestamp: 54312.619999999995,
        progress: 86,
      },
      {
        confidence: 0.3108140528202057,
        timestamp: 54329.619999999995,
        progress: 86,
      },
      {
        confidence: 0.3382070064544678,
        timestamp: 54395.619999999995,
        progress: 86,
      },
      {
        confidence: 0.3606526255607605,
        timestamp: 54462.619999999995,
        progress: 86,
      },
      {
        confidence: 0.33592477440834045,
        timestamp: 54545.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54612.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54679.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54695.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54712.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54762.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54779.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54812.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54828.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54845.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54862.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54895.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54912.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54928.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54945.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54962.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 54978.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55012.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55028.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55045.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55062.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55095.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55112.619999999995,
        progress: 87,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55145.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55162.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55178.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55195.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55212.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55228.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55262.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55278.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55295.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55312.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55345.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55362.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55378.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55395.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55412.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55428.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55445.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55462.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55495.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55512.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55528.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55545.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55562.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55578.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55595.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55612.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55628.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55645.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55662.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55678.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55695.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55712.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55728.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55745.619999999995,
        progress: 88,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55762.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55778.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55795.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55812.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55828.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55845.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55862.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55878.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55895.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55912.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55928.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55945.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55962.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55978.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 55995.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56012.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56028.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56045.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56062.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56078.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56095.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56112.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56128.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56145.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56162.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56178.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56195.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56212.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56228.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56245.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56262.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56278.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56295.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56312.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56328.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56345.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56362.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56378.619999999995,
        progress: 89,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56395.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56412.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56428.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56445.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56462.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56478.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56495.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56512.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56528.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56545.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56562.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56578.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56595.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56612.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56628.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56645.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56662.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56678.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56695.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56712.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56728.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56745.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56762.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56778.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56795.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56812.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56828.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56845.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56862.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56878.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56895.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56912.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56928.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56945.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56962.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56978.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 56995.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 57012.619999999995,
        progress: 90,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 57028.619999999995,
        progress: 91,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 57045.619999999995,
        progress: 91,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 57062.619999999995,
        progress: 91,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 57078.619999999995,
        progress: 91,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 57095.619999999995,
        progress: 91,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 57112.619999999995,
        progress: 91,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 57128.619999999995,
        progress: 91,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 57145.619999999995,
        progress: 91,
      },
      {
        confidence: 0.3675304651260376,
        timestamp: 57162.619999999995,
        progress: 91,
      },
      {
        confidence: 0.2024572640657425,
        timestamp: 57178.619999999995,
        progress: 91,
      },
      {
        confidence: 0.212687686085701,
        timestamp: 57228.619999999995,
        progress: 91,
      },
      {
        confidence: 0.22993284463882446,
        timestamp: 57311.619999999995,
        progress: 91,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57378.619999999995,
        progress: 91,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57445.619999999995,
        progress: 91,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57478.619999999995,
        progress: 91,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57495.619999999995,
        progress: 91,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57511.619999999995,
        progress: 91,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57528.619999999995,
        progress: 91,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57545.619999999995,
        progress: 91,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57561.619999999995,
        progress: 91,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57578.619999999995,
        progress: 91,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57595.619999999995,
        progress: 91,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57611.619999999995,
        progress: 91,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57628.619999999995,
        progress: 91,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57645.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57661.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57678.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57695.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57711.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57728.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57745.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57761.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57778.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57795.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57811.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57828.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57845.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57861.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57878.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57895.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57911.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57928.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57945.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57961.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57978.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 57995.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58011.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58028.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58045.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58061.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58078.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58095.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58111.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58128.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58145.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58161.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58178.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58195.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58211.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58228.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58245.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58261.619999999995,
        progress: 92,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58278.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58295.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58311.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58328.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58345.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58361.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58378.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58395.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58411.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58428.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58445.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58461.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58478.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58495.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58511.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58528.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58545.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58561.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58578.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58595.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58611.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58628.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58645.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58661.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58678.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58695.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58711.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58728.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58745.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58761.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58778.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58795.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58811.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58828.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58845.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58861.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58878.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58895.619999999995,
        progress: 93,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58911.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58928.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58945.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58961.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58978.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 58995.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59011.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59028.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59045.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59061.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59078.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59095.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59111.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59128.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59145.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59161.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59178.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59195.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59211.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59228.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59245.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59261.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59278.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59295.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59311.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59328.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59345.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59361.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59378.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59395.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59411.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59428.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59445.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59461.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59478.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59495.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59511.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59528.619999999995,
        progress: 94,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59544.619999999995,
        progress: 95,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59561.619999999995,
        progress: 95,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59578.619999999995,
        progress: 95,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59594.619999999995,
        progress: 95,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59611.619999999995,
        progress: 95,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59628.619999999995,
        progress: 95,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59644.619999999995,
        progress: 95,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59661.619999999995,
        progress: 95,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59678.619999999995,
        progress: 95,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59694.619999999995,
        progress: 95,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59711.619999999995,
        progress: 95,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59728.619999999995,
        progress: 95,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59744.619999999995,
        progress: 95,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59761.619999999995,
        progress: 95,
      },
      {
        confidence: 0.28044161200523376,
        timestamp: 59778.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 59794.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 59878.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 59894.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 59911.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 59928.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 59944.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 59961.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 59978.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 59994.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60011.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60028.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60044.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60061.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60078.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60094.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60111.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60128.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60144.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60161.619999999995,
        progress: 95,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60178.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60194.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60211.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60228.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60261.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60278.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60294.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60311.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60328.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60344.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60361.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60378.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60394.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60411.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60428.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60444.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60461.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60478.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60494.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60511.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60528.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60544.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60561.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60578.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60594.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60611.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60628.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60644.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60661.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60678.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60694.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60711.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60728.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60744.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60761.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60778.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60794.619999999995,
        progress: 96,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60811.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60828.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60844.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60861.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60878.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60894.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60911.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60928.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60944.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60961.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60978.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 60994.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61011.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61028.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61044.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61061.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61078.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61094.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61111.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61128.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61144.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61161.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61178.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61194.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61211.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61228.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61244.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61261.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61278.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61294.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61311.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61328.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61344.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61361.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61378.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61394.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61411.619999999995,
        progress: 97,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61428.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61444.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61461.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61478.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61494.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61511.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61528.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61544.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61561.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61578.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61594.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61611.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61628.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61644.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61661.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61678.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61694.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61711.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61728.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61744.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61761.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61778.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61794.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61811.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61827.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61844.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61861.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61877.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61894.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61911.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61927.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61944.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61961.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61977.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 61994.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 62011.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 62027.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 62044.619999999995,
        progress: 98,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 62061.619999999995,
        progress: 99,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 62077.619999999995,
        progress: 99,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 62094.619999999995,
        progress: 99,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 62111.619999999995,
        progress: 99,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 62127.619999999995,
        progress: 99,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 62144.619999999995,
        progress: 99,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 62161.619999999995,
        progress: 99,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 62177.619999999995,
        progress: 99,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 62194.619999999995,
        progress: 99,
      },
      {
        confidence: 0.6712847352027893,
        timestamp: 62211.619999999995,
        progress: 99,
      },
      {
        confidence: 0.4472118020057678,
        timestamp: 62227.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48411881923675537,
        timestamp: 62294.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62361.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62427.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62444.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62461.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62477.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62494.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62511.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62527.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62544.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62561.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62577.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62594.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62611.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62627.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62644.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62661.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62677.619999999995,
        progress: 99,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62694.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62711.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62727.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62744.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62761.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62777.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62794.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62811.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62827.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62844.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62861.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62877.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62894.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62911.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62927.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62944.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62961.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62977.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 62994.619999999995,
        progress: 100,
      },
      {
        confidence: 0.48755577206611633,
        timestamp: 63011.619999999995,
        progress: 100,
      },
    ],
    leftEyeImageData: undefined,
    rightEyeImageData: undefined,
    osdi: 27.083333333333332,
  };

  const reportPDF = useMemo(() => {
    const ageDifMs = Date.now() - new Date(dob).getTime();
    const ageDate = new Date(ageDifMs);
    const age = Math.abs(ageDate.getUTCFullYear() - 1970);
    return (
      <PDFReport
        name={`${fname} ${lname}`}
        age={age}
        gender={gender.slice(0, 1).toUpperCase() + gender.slice(1)}
        height={height}
        weight={weight}
        result={{
          timestamp: new Date(
            state?.scanResult?.entry_time?.replace?.(" ", "T") ?? 0
          ),
          bp_sys: state?.scanResult?.vitals.bp_sys,
          bp_dia: state?.scanResult?.vitals.bp_dia,
          resp_rate: state?.scanResult?.vitals.resp_rate,
          oxy_sat_prcnt: state?.scanResult?.vitals.oxy_sat_prcnt,
          heart_rate: state?.scanResult?.vitals.heart_rate,
          bmi: state?.scanResult?.metadata.physiological_scores.bmi,
          sdnn:
            state?.scanResult?.metadata.heart_scores.sdnn !== "--"
              ? Math.round(state?.scanResult?.metadata.heart_scores.sdnn)
              : "--",
          rmssd:
            state?.scanResult?.metadata.heart_scores.rmssd !== "--"
              ? Math.round(state?.scanResult?.metadata.heart_scores.rmssd)
              : "--",
          pNN50_per: state?.scanResult?.metadata.heart_scores.pNN50_per,
          cardiac_out: state?.scanResult?.metadata.cardiovascular.cardiac_out,
          map: state?.scanResult?.metadata.cardiovascular.map,
          heart_utilized:
            state?.scanResult?.metadata.heart_scores.heart_utilized,
          HRMax: state?.scanResult?.metadata.heart_scores.HRMax,
          HRR: state?.scanResult?.metadata.heart_scores.HRR,
          THRR: state?.scanResult?.metadata.heart_scores.THRR,
          vo2max: state?.scanResult?.metadata.physiological_scores.vo2max,
          bloodvolume:
            state?.scanResult?.metadata.physiological_scores.bloodvolume,
          tbw: state?.scanResult?.metadata.physiological_scores.tbw,
          tbwp: state?.scanResult?.metadata.physiological_scores.tbwp,
          bodyfat: state?.scanResult?.metadata.physiological_scores.bodyfat,
          weight: state?.scanResult?.metadata.physiological_scores.weight,
          stress: state?.scanResult?.metadata.heart_scores.stress_index,
          overallScore: state?.scanResult?.wellness_score,
          pulmoScore: state?.pulmoResult.pulmoScore,
          prq: state?.pulmoResult.prq,
          fev1fvcRatio: state?.pulmoResult.fev1fvcRatio,
          isGood: state?.pulmoResult.isOADScoreGood,
          hba1c: state?.scanResult?.metadata.glucose_info.hba1c,
        }}
      />
    );
  }, [state, fname, lname, dob, gender, height, weight]);

  return (
    <section className="pt-2">
      <div className="flex items-center m-6">
        <Link
          to={-2}
          className="w-9 h-9 mr-6 bg-[#2D2D2D] rounded-full flex justify-center items-center"
        >
          <img src={BackArrow} alt="back arrow" />
        </Link>
        <h2 className="text-xl font-medium">Assessment Result</h2>
      </div>
      <Tab.Group>
        <Tab.List
          className="
    relative z-50 mb-6 px-4 
    flex justify-start md:justify-center 
    space-x-3 overflow-x-auto scrollbar-hide
    w-full  pb-2
  "
        >
          {[
            "Cardiovascular Observation",
            "Pulmonary Observation",
            "Derma",
            "Allergy",
            "Vision",
            "PIVC",
          ].map((item) => (
            <Tab
              key={item}
              className={({ selected }) =>
                `
          whitespace-nowrap
          px-5 py-2 rounded-full text-sm font-medium transition
          ${
            selected
              ? "bg-white text-black shadow-md"
              : "bg-[#2D2D2D] text-white/70 hover:text-white hover:bg-[#3a3a3a]"
          }
        `
              }
            >
              {item}
            </Tab>
          ))}
        </Tab.List>

        <Tab.Panels>
          <Tab.Panel>
            <CardiovascularObservationResult result={state?.scanResult} />
          </Tab.Panel>
          <Tab.Panel>
            <div className="px-6 !mb-10 tab-screen">
              <ScoreData
                isGood={state?.pulmoResult.isOADScoreGood}
                fev1fvcRatio={state?.pulmoResult.fev1fvcRatio}
                pulmoScore={state?.pulmoResult.pulmoScore}
              />
            </div>
            <div className=" bg-white px-6 pt-3 pb-20">
              {audioQualityLevel < 3 && (
                <div className="mt-6 p-6 flex items-center space-x-4 rounded-[20px] bg-[#f8b62d]">
                  <svg
                    className="shrink-0 w-8"
                    viewBox="0 0 122.88 111.54"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill="#000"
                      d="M2.35,84.42,45.28,10.2l.17-.27h0A23,23,0,0,1,52.5,2.69,17,17,0,0,1,61.57,0a16.7,16.7,0,0,1,9.11,2.69,22.79,22.79,0,0,1,7,7.26q.19.32.36.63l42.23,73.34.24.44h0a22.48,22.48,0,0,1,2.37,10.19,17.63,17.63,0,0,1-2.17,8.35,15.94,15.94,0,0,1-6.93,6.6c-.19.1-.39.18-.58.26a21.19,21.19,0,0,1-9.11,1.75v0H17.61c-.22,0-.44,0-.65,0a18.07,18.07,0,0,1-6.2-1.15A16.42,16.42,0,0,1,3,104.24a17.53,17.53,0,0,1-3-9.57,23,23,0,0,1,1.57-8.74,7.66,7.66,0,0,1,.77-1.51Z"
                    />
                    <path
                      fill="#f8b62d"
                      d="M9,88.75,52.12,14.16c5.24-8.25,13.54-8.46,18.87,0l42.43,73.69c3.39,6.81,1.71,16-9.33,15.77H17.61C10.35,103.8,5.67,97.43,9,88.75Z"
                    />
                    <path
                      fill="#000"
                      d="M57.57,83.78A5.53,5.53,0,0,1,61,82.2a5.6,5.6,0,0,1,2.4.36,5.7,5.7,0,0,1,2,1.3,5.56,5.56,0,0,1,1.54,5,6.23,6.23,0,0,1-.42,1.35,5.57,5.57,0,0,1-5.22,3.26,5.72,5.72,0,0,1-2.27-.53A5.51,5.51,0,0,1,56.28,90a5.18,5.18,0,0,1-.36-1.27,5.83,5.83,0,0,1-.06-1.31h0a6.53,6.53,0,0,1,.57-2,4.7,4.7,0,0,1,1.14-1.56Zm8.15-10.24c-.19,4.79-8.31,4.8-8.49,0-.82-8.21-2.92-29.34-2.86-37.05.07-2.38,2-3.79,4.56-4.33a12.83,12.83,0,0,1,5,0c2.61.56,4.65,2,4.65,4.44v.24L65.72,73.54Z"
                    />
                  </svg>
                  <p className="text-xxs">
                    This assessment result is generated based on your Face scan
                    {audioQualityLevel === 2
                      ? ", Cough audio sample"
                      : audioQualityLevel === 1
                      ? ", Voice audio sample"
                      : ""}{" "}
                    and questionnaire responses. The{" "}
                    {audioQualityLevel === 2
                      ? "Voice"
                      : audioQualityLevel === 1
                      ? "Cough"
                      : "Voice & Cough"}{" "}
                    sample recording did not meet the required characteristics
                    and was excluded from the overall assessment computation.
                  </p>
                </div>
              )}
              <div className="tab-screen grid lg:grid-cols-2 md:grid-cols-2 grid-cols-1 lg:gap-4 md:gap-4 gap-3">
                <AudioSample
                  title="Voice"
                  audioURL={state?.aVowelAudioB64Data ?? ""}
                  onPoorQuality={() => setAudioQualityLevel((s) => s - 1)}
                />
                <AudioSample
                  title="Cough"
                  audioURL={state?.coughAudioB64Data ?? ""}
                  onPoorQuality={() => setAudioQualityLevel((s) => s - 2)}
                />

                <div className="mt-4 p-6 rounded-[20px] bg-[#1D1D1D] text-white">
                  <h3 className="mb-4 text-lg">Heart Rate</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                      data={state?.pulmoResult.hrTimeData.data}
                      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="chart-bar-gradient-hr"
                          x1="0%"
                          y1="50%"
                          x2="100%"
                          y2="50%"
                        >
                          <stop offset="25%" stopColor="#0EA573" />
                          <stop offset="75%" stopColor="#063BC4" />
                        </linearGradient>
                      </defs>
                      <XAxis
                        type="number"
                        dataKey="time"
                        allowDataOverflow={false}
                        domain={[0, 35]}
                        ticks={[5, 10, 15, 20, 25, 30]}
                        tickLine={false}
                        tickFormatter={(v) => `${v} sec`}
                        fontSize={10}
                        stroke="#fff"
                        strokeOpacity={0.75}
                        strokeWidth={1}
                      />
                      <YAxis
                        type="number"
                        dataKey="value"
                        domain={["dataMin - 8", "dataMax + 8"]}
                        tick={false}
                        tickMargin={0}
                        stroke="#fff"
                        strokeOpacity={0.75}
                        strokeWidth={1}
                        width={1}
                      />
                      <Line
                        type="monotone"
                        strokeWidth={6}
                        stroke="url(#chart-bar-gradient-hr)"
                        dataKey="value"
                        dot={false}
                      />
                      <ReferenceLine
                        x={10}
                        isFront={true}
                        stroke="#fff"
                        strokeOpacity={0.5}
                        strokeDasharray="3 3"
                      />
                      <ReferenceLine
                        x={25}
                        isFront={true}
                        stroke="#fff"
                        strokeOpacity={0.5}
                        strokeDasharray="3 3"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="mt-1 flex flex-row justify-between gap-6">
                    <div>
                      <h4 className="text-secondary text-sm font-semibold">
                        min HR:
                      </h4>
                      <p>
                        <span className="font-semibold">
                          {state?.pulmoResult.hrTimeData.data[
                            state?.pulmoResult.hrTimeData.min
                          ]?.value ?? "--"}
                        </span>
                        &nbsp;
                        <span className="text-xs">bpm</span>
                      </p>
                      <p className="text-xxs font-light">
                        ⏱️{" "}
                        {state?.pulmoResult.hrTimeData.data[
                          state?.pulmoResult.hrTimeData.min
                        ]?.time ?? "--"}{" "}
                        seconds
                      </p>
                    </div>
                    <div>
                      <h4 className="text-secondary text-sm font-semibold">
                        max HR:
                      </h4>
                      <p>
                        <span className="font-semibold">
                          {state?.pulmoResult.hrTimeData.data[
                            state?.pulmoResult.hrTimeData.max
                          ]?.value ?? "--"}
                        </span>
                        &nbsp;
                        <span className="text-xs">bpm</span>
                      </p>
                      <p className="text-xxs font-light">
                        ⏱️{" "}
                        {state?.pulmoResult.hrTimeData.data[
                          state?.pulmoResult.hrTimeData.max
                        ]?.time ?? "--"}{" "}
                        seconds
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-6 rounded-[20px] bg-[#1D1D1D] text-white">
                  <h3 className="mb-4 text-lg">Respiration Rate</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                      data={state?.pulmoResult.rrTimeData.data}
                      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="chart-bar-gradient-rr"
                          x1="0%"
                          y1="50%"
                          x2="100%"
                          y2="50%"
                        >
                          <stop offset="25%" stopColor="#063BC4" />
                          <stop offset="75%" stopColor="#0EA573" />
                        </linearGradient>
                      </defs>
                      <XAxis
                        type="number"
                        dataKey="time"
                        allowDataOverflow={false}
                        domain={[0, 35]}
                        ticks={[5, 10, 15, 20, 25, 30]}
                        tickLine={false}
                        tickFormatter={(v) => `${v} sec`}
                        fontSize={10}
                        stroke="#fff"
                        strokeOpacity={0.75}
                        strokeWidth={1}
                      />
                      <YAxis
                        type="number"
                        dataKey="value"
                        domain={["dataMin - 2", "dataMax + 2"]}
                        tick={false}
                        tickMargin={0}
                        stroke="#fff"
                        strokeOpacity={0.75}
                        strokeWidth={1}
                        width={1}
                      />
                      <Line
                        type="monotone"
                        strokeWidth={6}
                        stroke="url(#chart-bar-gradient-rr)"
                        dataKey="value"
                        dot={false}
                      />
                      <ReferenceLine
                        x={10}
                        isFront={true}
                        stroke="#fff"
                        strokeOpacity={0.5}
                        strokeDasharray="3 3"
                      />
                      <ReferenceLine
                        x={25}
                        isFront={true}
                        stroke="#fff"
                        strokeOpacity={0.5}
                        strokeDasharray="3 3"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="mt-1 flex flex-row justify-between gap-6">
                    <div>
                      <h4 className="text-secondary text-sm font-semibold">
                        min RR:
                      </h4>
                      <p>
                        <span className="font-semibold">
                          {state?.pulmoResult.rrTimeData.data[
                            state?.pulmoResult.rrTimeData.min
                          ]?.value ?? "--"}
                        </span>
                        &nbsp;
                        <span className="text-xs">breathes/min</span>
                      </p>
                      <p className="text-xxs font-light">
                        ⏱️{" "}
                        {state?.pulmoResult.rrTimeData.data[
                          state?.pulmoResult.rrTimeData.min
                        ]?.time ?? "--"}{" "}
                        seconds
                      </p>
                    </div>
                    <div>
                      <h4 className="text-secondary text-sm font-semibold">
                        max RR:
                      </h4>
                      <p>
                        <span className="font-semibold">
                          {state?.pulmoResult.rrTimeData.data[
                            state?.pulmoResult.rrTimeData.max
                          ]?.value ?? "--"}
                        </span>
                        &nbsp;
                        <span className="text-xs">breathes/min</span>
                      </p>
                      <p className="text-xxs font-light">
                        ⏱️{" "}
                        {state?.pulmoResult.rrTimeData.data[
                          state?.pulmoResult.rrTimeData.max
                        ]?.time ?? "--"}{" "}
                        seconds
                      </p>
                    </div>
                  </div>
                </div>

                <div className="col-span-full">
                  <div className="mt-4 px-6 py-4 flex items-center justify-between space-x-4 rounded-[20px] bg-[#1D1D1D]">
                    <h3 className="text-white font-semibold">
                      Pulse-Respiration Quotient (PRQ)
                    </h3>
                    <p className="text-secondary text-2xl font-semibold">
                      {state?.pulmoResult.prq || "--"}
                    </p>
                  </div>
                  <Link
                    to={-2}
                    className="block mt-8 px-6 py-3 lg:w-1/2 md:w-1/2 w-full mx-auto rounded-full outline-none border border-primary/80 bg-white text-primary text-center text-sm font-light"
                  >
                    Back
                  </Link>
                </div>
              </div>
            </div>
          </Tab.Panel>
          <Tab.Panel>
            <DermaResult state={dermaResult1} />
          </Tab.Panel>
          <Tab.Panel>
            <AssessmentResult state={allergyResult} />
          </Tab.Panel>
          <Tab.Panel>
            <VisionResult state={visionAi} />
          </Tab.Panel>
          <Tab.Panel>
            <PIVCResult />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
      <PDFDownloadLink
        className="flex items-center justify-between space-x-4 py-3 px-6 rounded-xl bg-black text-white text-sm font-medium"
        document={reportPDF}
        fileName="Unity-Health-Report"
      >
        {({ loading }) => (
          <>
            <span>
              {loading ? "Loading document..." : "Download PDF Report"}
            </span>
            <Download className="flex-shrink-0 h-6 w-6" />{" "}
          </>
        )}
      </PDFDownloadLink>
    </section>
  );
};

export default PulmonaryResult;
